// src/services/aeo/types.ts
//
// Wire contract for the AEO (Answer Engine Optimization) Assessment API —
// ourteam-frontend#3065, BE epic ourteam-backend#3618. Field names and shapes
// mirror the ticket's JSON exactly (snake_case) so the service layer in
// ./index.ts is a thin, low-risk pass-through: the day the real BFF proxy
// ships, these types should not need to change.

import type { PublishRefusal } from "./deliveryTypes";
import type { ClientLifecycle } from "./clientReview";

export interface AeoAnchors {
    primary: string[];
    adjacent?: string[];
    branches?: string[];
    city?: string;
}

export interface AeoPrescreenRequest {
    domain: string;
    business_name: string;
    category?: string;
    anchors: { primary: string[]; city?: string };
}

export type AeoPrescreenVerdict = "VISIBLE" | "PARTIAL" | "INVISIBLE";

export interface AeoPrescreenResponse {
    verdict: AeoPrescreenVerdict;
    engines_checked: string[];
    notes: string;
}

/** Body for POST /detect (ourteam-backend#3687). Domain must already be a bare
 *  lowercased hostname — no protocol, no `www.` (the BE re-normalizes anyway). */
export interface AeoDetectRequest {
    domain: string;
}

/**
 * Response of POST /detect — a quick crawl of the homepage (plus one cheap
 * about/contact fetch) so staff CONFIRM the business details instead of typing
 * them. Always HTTP 200: an unreachable or low-signal site comes back with
 * `confidence: "low"` and empty fields rather than an error, and the BE never
 * fabricates — anything not actually found on the site stays empty.
 *
 * Mirrors `detect_business()` in app/dependencies/aeo_detect.py exactly.
 */
export interface AeoDetectedBusiness {
    business_name: string;
    category: string;
    /** `primary` is seeded with the detected name; `city` is "" when not found. */
    anchors: { primary: string[]; city: string };
    /** Capped at 8 by the BE. */
    services: string[];
    /** "high" only when the BE resolved a name AND a category or city. */
    confidence: "high" | "low";
    /**
     * Why nothing was prefilled, when the BE refused to (ourteam-backend#5906):
     * `NOT_THE_BUSINESS_SITE` means the domain is serving a hosting, parked or
     * placeholder page, not the business's own site. Absent or null on a normal
     * read, and on a backend that does not send it yet.
     */
    reason_code?: string | null;
    /** The plain sentence that goes with `reason_code`, written for the operator. */
    reason?: string | null;
}

export interface AeoCreateAssessmentRequest {
    domain: string;
    /** Who asked for this run (#3509, BE ourteam-backend#4568). A label, not an
     *  identity — the BE separately records the authenticated caller. */
    requested_by?: string;
    business_name: string;
    category?: string;
    anchors: AeoAnchors;
    services: string[];
    languages?: string[];
    competitors?: string[];
    webhook_url?: string;
}

export interface AeoCreateAssessmentAccepted {
    assessment_id: string;
    status: "queued";
    estimated_cost_usd: number;
    estimated_duration_minutes: number;
    queue_position: number;
}

/**
 * `createAssessment` never throws for the two expected non-2xx outcomes
 * (400 validation, 409 duplicate-in-flight) — both are flow branches the
 * screen renders, not errors. Anything else collapses to `kind: "error"`.
 */
export type AeoCreateResult =
    | { ok: true; data: AeoCreateAssessmentAccepted }
    | { ok: false; kind: "duplicate"; assessment_id: string }
    | { ok: false; kind: "validation"; errors: string[] }
    | { ok: false; kind: "error"; message: string };

/**
 * `cancelled` is a FIRST-CLASS terminal state, not a flavour of `failed`
 * (ourteam-backend#3982). A run someone deliberately stopped is not a
 * malfunction, and the UI must not tell an operator to report their own
 * decision to engineering. It carries `results` when the run got far enough to
 * produce any — a cancelled run is not necessarily worthless.
 */
export type AeoAssessmentStatus =
    | "queued" | "running" | "complete" | "partial" | "failed" | "cancelled";

/**
 * POST /api/v1/assessments/{id}/cancel (ourteam-backend#3982).
 *
 * `already_terminal` is a 409 and is NOT an error worth alarming about: the run
 * finished in the gap between rendering the button and the click landing. The
 * caller shows the real outcome instead of a failure.
 */
export type AeoCancelResult =
    | { ok: true; status: AeoAssessmentStatus; stage: string | null; cancelled_at: string | null }
    | { ok: false; kind: "already_terminal" }
    | { ok: false; kind: "not_found" }
    | { ok: false; kind: "error" };

export interface AeoProviderVisibility {
    mention_rate: number;
    citation_rate: number;
    /** Always present; can be < 50 under quota — display it, never assume 50. */
    responses_sampled: number;
}

export interface AeoVisibility {
    openai: AeoProviderVisibility;
    gemini: AeoProviderVisibility;
    share_of_voice: number;
}

export interface AeoAudit {
    overall_score: number;
    pages_scraped: number;
    mode: "full" | "visibility_only";
}

export interface AeoCompetitor {
    name: string;
    queries_appeared: number;
}

/**
 * WHICH instrument produced the visibility number (#3564, BE #4610).
 *
 * Oura used to ask the OpenAI API. It now asks the real ChatGPT app and
 * screenshots every answer, and the two disagree badly — 18/50 via the API
 * against 33/50 via the app for the same business in the same week, because the
 * API reads a general web crawl while the app answers local questions from a
 * Maps place-card. Clients check the app, so the app is the number that has to
 * be right, and the label is the difference between a figure a client can
 * reproduce and one they cannot.
 *
 * Absent on a scan from before this shipped. The score still renders; it simply
 * says nothing about how it was measured, which is what it did all along.
 */
export interface AeoGrounding {
    /** `chatgpt_app` | `openai_api` — machine value, never shown. */
    instrument: string;
    /** Shown VERBATIM. May end in ", partial", which means only some questions
     *  reached the app and the rest are API answers — not a clean figure. */
    instrumentLabel: string;
    /**
     * Set when the run fell back to the API because the browser was
     * unavailable. The number then means something different and the reader has
     * to be told: this is the case that quietly loses trust.
     */
    instrumentNote?: string | null;
    /**
     * True when the run used BOTH instruments — mostly the browser with API
     * answers filling the gaps, or the reverse. It marks the two middle cases
     * of `instrumentLabel` ("ChatGPT (app), 44 of 50" / "OpenAI (API), 38 of
     * 50"), where the figure is not a clean measurement from either one.
     */
    mixed?: boolean;
    askedQuestions?: number;
    totalQuestions?: number;
    grounded?: number;
    failed?: number;
    costUsd?: number;
    /**
     * False when this instrument cannot see citations at all. The citation
     * metrics must then be HIDDEN, never rendered as 0 — zero would be a false
     * claim about the client's own site.
     */
    citationsMeasurable?: boolean;
}

export interface AeoResults {
    visibility: AeoVisibility;
    /** How the visibility number was measured (#3564). */
    grounding?: AeoGrounding | null;
    audit: AeoAudit;
    maturity_level: number;
    maturity_label: string;
    top_competitors: AeoCompetitor[];
    actual_cost_usd: number;
}

/**
 * The inputs a run was CREATED with (ourteam-backend#4567), so a re-run can
 * reproduce it instead of re-deriving from a fresh detect crawl.
 *
 * `null` when the run stored nothing, which is deliberately distinct from a run
 * that genuinely had none: only the first justifies falling back to detect.
 */
export interface AeoSubmittedInputs {
    anchors: AeoAnchors;
    services: string[];
}

/** One pipeline stage and where the run has got to with it. */
export interface AeoAssessmentStage {
    /** The backend's stage name (`run_engines`), not a label. Wording is the
     *  frontend's job — see `stageLabel()`. */
    name: string;
    state: "done" | "active" | "pending";
    /** When it finished. Null on a run from before timings were recorded, which
     *  is not the same as "finished at an unknown time" — the stage still shows
     *  as done, just without a clock. */
    completed_at?: string | null;
}

/**
 * An estimate built from how long past runs took, never a promise.
 *
 * `remaining_seconds` is null when the backend will not stand behind a number:
 * too few comparable runs, or a run that has already overrun its estimate. Null
 * must render as nothing at all — a timer stuck on "1 minute left" for twenty
 * minutes is worse than no timer.
 */
export interface AeoAssessmentEta {
    remaining_seconds: number | null;
    /** How tight the spread was. "wide" earns a range rather than a figure. */
    confidence?: "typical" | "wide" | null;
    based_on_runs?: number | null;
}

export interface AeoAssessment {
    assessment_id: string;
    /**
     * Shipped in ourteam-backend#3743 — `to_status_dict()` now includes these.
     * Still optional: `src/oura/aeo.ts` keeps its own copy from the create
     * request for jobs polled before this shipped, and the type shouldn't
     * assume every historical caller has it.
     */
    domain?: string;
    /** The path the site lives under on a shared host (#4832); "" or absent for the site root. Scans of one business share it (#4773). */
    base_path?: string | null;
    business_name?: string;
    /** Also shipped in #3743's `to_status_dict()` addition; `""` when none was submitted. */
    category?: string;
    status: AeoAssessmentStatus;
    stage?: string;
    stages_complete?: number;
    stages_total?: number;
    /**
     * The pipeline in order, with what each stage is doing (#3572, BE
     * ourteam-backend#4643).
     *
     * The ORDER of this array is the pipeline order, and it is the only source
     * of it the frontend has. `stageLabels.ts` maps a name to wording and says
     * outright that its own order must never be read as a sequence — AEO_STAGES
     * has grown twice, and a positional read renames every stage after the
     * insertion while the count still looks right.
     *
     * Absent on a backend that predates the change; the progress surfaces then
     * fall back to the step counter they have always used.
     */
    stages?: AeoAssessmentStage[] | null;
    /** Roughly how much longer, or null when we cannot say honestly. */
    eta?: AeoAssessmentEta | null;
    created_at: string;
    started_at?: string;
    completed_at?: string;
    results?: AeoResults;
    /** What this run was created with (#4567) — see AeoSubmittedInputs. */
    inputs?: AeoSubmittedInputs | null;
    /** Who asked for this run (ourteam-backend#4568); null on runs predating it. */
    requested_by?: string | null;
    report_url?: string | null;
    /** e.g. "ENGINE_STUBBED" on `partial`, or a failure code on `failed`. */
    error_code?: string;
    error_detail?: string;
    /**
     * Why a PARTIAL scan is partial, in a sentence written for staff
     * (ourteam-backend#5411 BE-3). Present only on `partial`, and worked out
     * when the payload is read, so a scan that finished before that change has
     * one too.
     *
     * `error_code` and `error_detail` stay beside it and are NOT the same
     * thing: those are for re-run and Delivery, they name only the FIRST
     * degradation of several, and `error_detail` can carry a stack trace. A
     * screen shows this and not those (#4025 FE-1).
     */
    notice_code?: string | null;
    notice_detail?: string | null;
}

/**
 * The `latest_assessment` block embedded on each `GET /api/v1/businesses` row
 * (ourteam-backend#3747) — enough of the newest terminal assessment to render
 * the Businesses table without a per-row fetch. `null` only when the business
 * has no terminal assessment yet (still queued/running, or none ever run).
 */
export interface AeoBusinessLatestAssessment {
    assessment_id: string;
    status: AeoAssessmentStatus;
    captured_at: string;
    /** 0-100. `0` is a real score — never treat it as "missing". */
    aeo_score: number;
    seo_score: number;
    /** true = the run produced no usable score; render as gated, not as 0. */
    gated: boolean;
    /**
     * Why the run produced nothing usable. `to_status_dict()` sends all four on
     * a failed or partial run and has since #3618 — the FE simply never read
     * them, so "Gated" was a dead end that could only be diagnosed in DevTools.
     *
     * Absent on a healthy run, and absent on the LIST payload
     * (`latest_terminal_summaries` pulls only the two scores via a JSONB path to
     * keep the row small) — they arrive with `GET /businesses/{id}`.
     */
    error_code?: string;
    error_detail?: string;
    /** The stage the run stopped on — the most useful single field of the four. */
    stage?: string;
    /**
     * Why a PARTIAL scan is partial, in a sentence written for staff
     * (ourteam-backend#5411 BE-3). Present only on `partial`, and worked out
     * when the payload is read, so a scan that finished before that change has
     * one too.
     *
     * `error_code` and `error_detail` stay beside it and are NOT the same
     * thing: those are for re-run and Delivery, they name only the FIRST
     * degradation of several, and `error_detail` can carry a stack trace. A
     * screen shows this and not those (#4025 FE-1).
     */
    notice_code?: string | null;
    notice_detail?: string | null;

    /** Who asked for this scan (#3509). Null on runs predating ourteam-backend#4568. */
    requested_by?: string | null;
    engine_version?: string;
}

/**
 * How customers buy: at the business's outlets, online, both, or the business
 * goes to them. The New scan's four answers, the same values as its `BuyMode`.
 */
export type AeoBuyMode = "visit" | "online" | "both" | "we_go_to_them";

/** Which Businesses tab a row sits under, decided by the server (ourteam-backend#6091). */
export type AeoListStage = "active" | "onboarding" | "prospect" | "ended";

/** `GET /api/v1/businesses` `data.stageCounts`: the tab counts, from the same rows (ourteam-backend#6091). */
export type AeoStageCounts = Record<AeoListStage | "all", number>;

/** One row of `GET /api/v1/businesses` / `/businesses/mine` (list shape — no `results`). */
export interface AeoBusiness {
    id: string;
    domain: string;
    /** The path the site lives under on a shared host (ourteam-backend#4832); "" for the site root. */
    base_path?: string;
    /** The site's own icon, found during its newest scan that looked (ourteam-backend#5901). Absent or null: none known. */
    favicon_url?: string | null;
    business_name: string;
    category: string;
    city: string;
    state: "prospect" | "client" | "churned";
    plan: "track" | "growth" | "multi" | null;
    latest_assessment: AeoBusinessLatestAssessment | null;
    /** When the business entered the pipeline (ourteam-backend#3793). Distinct
     *  from `latest_assessment.captured_at`, which is when it was last scanned. */
    created_at: string;
    /**
     * The Oura client workspace that owns this business — the `company_id` the
     * BE already stores on `aeo_businesses` and uses in `_ensure_client_workspace`.
     *
     * Optional because `AeoBusiness.to_dict()` does not serialize it yet
     * (ourteam-backend#4755). Absent means "no merchant workspace exists, or this
     * backend predates the field", and every consumer must treat it as OFF rather
     * than guessing an id — a wrong company_id would switch a staffer into someone
     * else's workspace.
     */
    company_id?: string | null;
    /**
     * Where this business's plan is (ourteam-backend#4915).
     *
     * `null` means no engagement at all — nobody has sold them anything. That is
     * a different fact from `"draft"`, and the two must not collapse: the plan
     * queue is businesses that ARE signed and have no published timeline, and a
     * prospect is neither.
     *
     * In the list payload rather than fetched per row, for the same reason the
     * scores are: the queue carries a count, which means the whole set and not
     * the page on screen.
     */
    engagement_status?: "draft" | "signed" | "active" | "ended" | "cancelled" | null;
    /** The newest published timeline version, or `null` when none has ever been
     *  published — which, with `engagement_status: "signed"`, IS the queue. */
    timeline_published_version?: number | null;
    /** An edit has landed on the plan that the merchant has not been told about. */
    timeline_change_pending?: boolean;
    /**
     * Which state the business is in, derived by the server: the same block
     * the client page already carries (#4807), on every list row.
     *
     * The plan fields below ride with it for the same reason the scores do: the
     * Businesses tabs carry counts, which means the whole set and not the page
     * on screen. All optional until the list sends them (BE handoff "Plan dates
     * on the business list"); absent, the tabs fall back to `engagement_status`
     * and onboarding, see `listStageOf`.
     */
    lifecycle?: ClientLifecycle | null;
    /**
     * The tab this row sits under, decided by the server (ourteam-backend#6091):
     * ended, then active (signed and started, even with onboarding open), then
     * onboarding, then prospect. Absent from an older server: `listStageOf` decides.
     */
    stage?: AeoListStage | null;
    /** The signed plan's name as the contract states it ("Annual plan"). Null with no engagement. */
    plan_name?: string | null;
    plan_term_months?: number | null;
    /** DATE-ONLY. Null until payment clears and the clock starts. */
    plan_started_on?: string | null;
    /**
     * DATE-ONLY, the term's last day: the PUBLISHED term end when a timeline
     * has been published, because that is the date the merchant was told,
     * otherwise the anniversary rule on `plan_started_on`.
     */
    plan_ends_on?: string | null;
    /** Which month of the term it is, computed by the server (`plan_month`). Read, never recomputed. */
    plan_month?: number | null;
    /**
     * How customers buy, and for an online business the country it sells to
     * (`market`). Optional until the list sends them (same BE handoff); absent,
     * `buyOf` reads a whole country in `city` as online, the backend's own rule.
     */
    buy?: AeoBuyMode | null;
    market?: string | null;
    /**
     * The last time we DID something for this business — a state change, a pack
     * published, a report delivered, a tracker task finished, a change logged
     * (ourteam-backend#4908).
     *
     * Rides in the list payload for the same reason the scores do: the table
     * sorts across every row, and a per-row fetch for 200 businesses is what
     * that rules out.
     *
     * `null` is a real answer — nothing has ever been delivered — and it sorts
     * as the quietest there is, not as the most recent.
     */
    last_delivery_event_at?: string | null;
    /**
     * Whether this is a category we sell AEO to (ourteam-backend#4910).
     *
     * `null` when the business has never been scanned — the block is written at
     * scan creation, so there is genuinely nothing to report. That is a
     * different fact from `priority_tier: 0`, which is "scanned, and detect
     * returned no category to score against", and the two must not collapse.
     */
    scope?: AeoScope | null;
}

/**
 * The scope verdict, as `aeo_scope.scope_block` writes it.
 *
 * WARN, NEVER BLOCK is the backend's rule and nothing here changes it: the tier
 * does not refuse a scan. Activating a paid plan is a different decision, and
 * the gate on that is a staff confirmation with a recorded reason, never a
 * server refusal.
 */
export interface AeoScope {
    /** 1 priority · 2 worth scanning · 3 low · 0 unknown category. */
    priority_tier: number;
    /** The sentence the backend already writes for a person. Render THIS. */
    priority_label: string;
    /** Set only on tier 3. */
    scope_warning: string | null;
    /** The category the tier was derived from, or null. */
    category_scored: string | null;
}

/** `results.businessAudit.competitors[]` — `build_business_audit()` / `_competitors()`
 *  in app/dependencies/aeo_overview.py. Distinct from `AeoCompetitor` (the
 *  assessment-level `top_competitors[]`, a different, narrower shape). */
export interface AeoBusinessCompetitor {
    name: string;
    domain: string;
    /** `null` = not scored in this run (the join to per-rival scores found nothing). */
    aeo: number | null;
    /** **`null` = not measured**, and that is the normal case (ourteam-backend#3828).
     *  Nothing in the platform measures a competitor's search performance — it
     *  needs a SERP vendor — and the number that used to sit here was parsed out
     *  of a Gemini reply. `0` is no longer sent; treat it as a real score only
     *  if something genuinely measured one. */
    seo: number | null;
    /** Questions this rival was named on. `null` = not scored in this run. */
    queries: number | null;
    note: string;
}

/** `businessAudit.priorityPages[]` — `_priority_pages()`. `visits` is GSC-backed
 *  and is `0` when no Search Console connection exists, which the `note` field
 *  distinguishes ("Most internally-linked pages" vs "Top pages by traffic"). */
export interface AeoPriorityPage {
    url: string;
    title: string;
    visits: number;
    note: string;
    pageKind?: PageKind;
}

/**
 * What kind of URL this is (ourteam-backend#4097).
 *
 * Anything other than `content` is a generated index — a category, tag, author
 * or date archive, a `/page/2`, a feed, a search result — and has no copy of its
 * own to improve. Oura offering to rewrite `/category/uncategorized` is the
 * #3954 failure in a new place.
 *
 * The BE detects these two ways, and the second is the half the FE cannot do:
 * URL patterns (multilingual, not WordPress-specific), **and** a link-word ratio
 * from the crawl — a page whose text is ≥60% inside links across ≥10 internal
 * links is an index whatever its slug says, which catches a clean-slug
 * `/updates` listing that no pattern would.
 *
 * Optional because older runs predate it. Absent means "unknown", and unknown
 * must be treated as `content`: dropping a page on a guess hides real work.
 */
export type PageKind =
    | "content"
    | "taxonomy"
    | "archive"
    | "paginated"
    | "feed"
    | "search"
    /** Demo copy a template shipped with and nobody replaced (#4117). A real
     *  page at a real URL, so it belongs in the inventory. */
    | "placeholder"
    /** The crawl could not read it: a 4xx, a 5xx, or a fetch that failed. NOT
     *  "we do not know what this is" — it is a page we know is broken, which is
     *  precisely a finding worth showing. */
    | "unknown";

/** `businessAudit.underperforming[]` — `_underperforming()`. The BE emits only
 *  "404", "Orphan page" and "Thin content" today (`_UNDERPERF_REASON`). */
export interface AeoUnderperformingPage {
    url: string;
    title: string;
    reason: string;
    visits: number;
    /** Indexes are **labelled here, not dropped** (ourteam-backend#4097): a broken
     *  category page is worth knowing about on the crawl-health surface, so the BE
     *  keeps it and the workshop filters it out. */
    pageKind?: PageKind;
}

/** `businessAudit.recommendations[]` — `_recommendations()`, derived from the
 *  audit checks and ordered by severity. */
export interface AeoRecommendation {
    id: string;
    title: string;
    impact: string;
    priority: "high" | "medium" | "low";
    effort: string;
    horizon: string;
    /**
     * The status of the tracker task that resolves this finding (#4528).
     *
     * The join is `rec_<check key>` against a check-sourced task's `source.ref`
     * — it always existed and nothing was reading it, so a task somebody marked
     * done still read as outstanding work here while the Tracker said otherwise.
     *
     * `null` is a THIRD answer, not a missing one: no task covers this check, so
     * the card must not imply a plan that does not exist. Computed on read,
     * because a task's status changes long after the run that produced the card.
     */
    workState?: "not_started" | "in_progress" | "blocked" | "done" | null;
    /** What to show for `workState` — "Done, awaiting re-crawl", "In progress",
     *  "Blocked". Null for `not_started`, which needs no label. */
    workStateLabel?: string | null;
    /**
     * The card no longer needs attention. It is NEVER removed: a check closes
     * when the next crawl reports zero, but a human marks the task done days
     * earlier, so a card that vanished at that moment would disappear while the
     * finding is still measurably present.
     */
    dim?: boolean;
}

/**
 * Traffic insight blocks on `businessAudit.traffic` (ourteam-backend#6426).
 * Each key is ABSENT when it was not measured. `aiReferrals: []` is the one
 * empty list that means something: GA4 answered and no visit came from an AI
 * assistant, a measured zero.
 */
export interface AeoTrafficInsights {
    /** Visits by GA4 default channel group, current and previous 28 days. */
    sources?: { channel: string; sessions: number | null; prevSessions: number | null }[];
    /** Visits from AI assistants, by canonical host (`chatgpt.com`, `perplexity.ai`, …). */
    aiReferrals?: { source: string; sessions: number | null; prevSessions: number | null }[];
    landingPages?: {
        page: string;
        sessions: number | null;
        aiSessions: number | null;
        keyEvents: number | null;
        prevSessions: number | null;
        prevKeyEvents: number | null;
    }[];
    /** GA4 key events (enquiries, bookings), current and previous 28 days. */
    keyEvents?: { count: number | null; prevCount: number | null };
    /** Oldest first. `work` comes from what Oura published; `staff` was added by
     *  hand and is the only kind with an id. Absent when they could not be read. */
    markers?: AeoTrafficMarker[];
}

export interface AeoTrafficMarker {
    id: string | null;
    /** The day in Malaysia, YYYY-MM-DD. */
    date: string;
    label: string;
    kind: "work" | "staff";
}

/** `businessAudit.traffic` — `_traffic()`. **Absent entirely** unless a
 *  Google Analytics 4 connection supplied real numbers (the source is
 *  `_ga4_pull`, not Search Console); the BE never fabricates it. */
export interface AeoTraffic extends AeoTrafficInsights {
    /** Sessions, last 28 days. */
    visits: number;
    unique: number;
    pagesPerVisit: number;
    /** A FRACTION, 0..1 — GA4's own unit. The adapter turns it into a percent. */
    bounceRate: number;
    /** Percent change on the window before, one decimal (ourteam-backend#4942).
     *  `null` when there is no earlier window to compare. A run stored before
     *  #4942 carries a placeholder `0.0` with an empty `trend`, which is why
     *  the adapter keeps the delta only beside a trend. */
    deltaPct: number | null;
    /** Weekly sessions, oldest first; `[]` on a run from before #4942. The
     *  same measurement as `series.weekly`, which is the one to read when
     *  present. */
    trend: number[];
    /** The window `visits` covers, as the BE measured it. */
    window?: { start: string; end: string; days: number } | null;
    /**
     * One series per granularity (ourteam-backend#5018), each point carrying
     * its own date. Absent until that ships, and a granularity GA4 cannot fill
     * is absent from the object — never zero-filled, and never derived from
     * another one. Deriving is what put "132 sessions this month" on a card
     * headlined 38.
     */
    series?: {
        daily?: { date: string; sessions: number }[] | null;
        weekly?: { start: string; sessions: number }[] | null;
        monthly?: { start: string; sessions: number }[] | null;
    } | null;
}

/**
 * `results.searchQuestions` — what Google is actually asked about this business
 * (ourteam-backend#4981, epic #4979).
 *
 * Two lists off one 12-month Search Console pull:
 *   • `matches` — a real search that landed on one of the 50 question slots.
 *   • `searches` — the full judged set, including the ones no slot covers.
 *
 * NOT CONNECTED IS `{connected: false}` AND NOTHING ELSE. The backend never
 * sends an empty `matches` as if it were measured, and neither may a screen
 * infer one: Google hides low-volume searches, so a question with no match is
 * unmeasured, never a zero.
 *
 * `rank` is the 1-based position among judged searches by yearly volume, taken
 * AFTER branded, low-volume, out-of-band and out-of-market searches are
 * dropped — so it is not the row number Google returned. The FE tiers on it
 * (1-10, 11-50) and hides the rest.
 */
export interface AeoSearchQuestions {
    connected: boolean;
    window?: { start: string; end: string; days: number } | null;
    fetchedAt?: string | null;
    matches?: AeoSearchMatch[];
    searches?: AeoSearchRow[];
    /** Why judged searches never reached the lists. Counts only. */
    dropped?: { branded: number; belowFloor: number; outOfBand: number; outOfMarket: number } | null;
    /** How many rows Search Console returned before anything was dropped
     *  (ourteam-backend#5053). The `dropped` counts mean nothing without it. */
    rowsSeen?: number | null;
    /**
     * The yearly-impression floor a query had to clear, as the backend applied
     * it. It travels with the counts because 276 below the floor is a different
     * story at 100 a year than at 1,000 — and a reader who has to know the
     * constant to read the number has been handed a puzzle, not an answer.
     * NEVER hard-code it here.
     */
    searchFloor?: number | null;
    /**
     * WHY `searches` is empty: `no_rows` · `all_branded` · `all_below_floor` ·
     * `all_out_of_band` · `all_out_of_market` · `mixed` (#5053).
     *
     * Null whenever `searches` is non-empty, the same contract as
     * `siteAnswersAbsent`. ABSENT ENTIRELY is a scan from before the backend
     * sent it, and that one keeps today's silent empty state — there is nothing
     * in it to say which of the three situations produced the empty block.
     */
    searchesAbsent?: string | null;
    /** The backend's own sentence for that reason. Rendered verbatim when it is
     *  there; the FE only composes one when it is not. */
    searchesAbsentDetail?: string | null;
    /**
     * How much of the "does your site answer it" judgement actually ran
     * (ourteam-backend#5034). `judged + sum(absent) === total`, always.
     */
    siteAnswersCoverage?: {
        total: number;
        judged: number;
        answered: number;
        notAnswered: number;
        absent: Record<string, number>;
    } | null;
    /** The backend's own sentence per absent reason, for the reasons this block
     *  used. Rendered verbatim — a rewrite here would be the screen inventing a
     *  diagnosis it did not make. */
    siteAnswersAbsentDetail?: Record<string, string> | null;
}

/** One real search that landed on one of the 50 slots. `slot` is the question's `n`. */
export interface AeoSearchMatch {
    slot: number;
    query: string;
    /** Searches over the 12-month window. */
    searches: number;
    clicks: number;
    /** Google's average position for it. */
    position: number;
    rank: number;
}

/** One judged search, covered by a question or not. */
export interface AeoSearchRow {
    query: string;
    rank: number;
    searches: number;
    clicks: number;
    position: number;
    /** The page Google shows for it, when there is one. */
    page?: string | null;
    /**
     * Whether the site's own page answers the search.
     *
     * NULL MEANS UNKNOWN, not "no". Every row on the first shipped scan
     * (ourteam-backend#4981) arrived null — the judgement is not computed yet —
     * and rendering that as a red "No" would tell 160 merchants their pages
     * fail a test nobody ran.
     */
    siteAnswers: boolean | null;
    /**
     * WHICH KIND OF NOTHING, when `siteAnswers` is null (ourteam-backend#5034).
     * `no_page` · `not_crawled` · `no_body_text` · `scan_predates_check`. Exactly
     * one of this and `siteAnswers` is set. The sentence for it lives once on
     * the block, in `siteAnswersAbsentDetail` — never rewritten here.
     */
    siteAnswersAbsent?: string | null;
    /** The question `n` covering it, or null when the 50 miss it. */
    coveredBy: number | null;
    /**
     * What adding this search to the set would do. `allowed: false` means the
     * recipe forbids it and `reason` is the recipe's own sentence — rendered
     * verbatim, never rewritten.
     */
    candidate?: {
        variable: string;
        value: string;
        replaces: string | null;
        allowed: boolean;
        reason: string | null;
    } | null;
}

/** One fill queued against a question slot (ourteam-backend#4982). */
export interface AeoQueuedFill {
    variable: string;
    value: string;
    replaces: string | null;
    queuedAt?: string | null;
    queuedBy?: string | null;
}

/** `results.businessAudit` — the executive summary block (#3683). */
/** `businessAudit.siteHealthScoreAbsent` — why no site-health score exists. */
export type AeoCrawlAbsence = "crawl_failed" | "crawl_blocked" | "not_crawled";

/**
 * Why `aiReadabilityScore` is null, when it is (`ai_readability()` in
 * aeo_audit.py). Four different facts with four different fixes — never a
 * shrug, and never a 0 standing in for any of them.
 *
 * `crawl_did_not_run`  no pages, so there is no share to take.
 * `crawl_blocked`      too few readable pages — the floor Site Health is
 *                      withheld on too (ourteam-backend#4803).
 * `shell_unrendered`   the site is client-rendered and its DOM was never built,
 *                      so every component reads zero. NOT a low score.
 * `no_components_yet`  none of the checks that feed it (B1-B5) is in the
 *                      catalogue on this run. Today's state for every business.
 */
export type AeoReadabilityAbsence =
    | "crawl_did_not_run"
    | "crawl_blocked"
    | "shell_unrendered"
    | "no_components_yet";

/** One `businessAudit.aiReadabilityComponents[]` row. */
export interface AeoReadabilityComponent {
    key: "headings" | "entity_markup" | "prices" | "staff" | "branches";
    /** The BE's own wording, e.g. "Price coverage". */
    label: string;
    /** Pages that pass. */
    covered: number;
    /** The pages this component applies to — **not** the crawl size. Price
     *  coverage is over treatment pages, staff attribution over staff pages. */
    applicable: number;
    /** 0-100, `covered / applicable`. */
    score: number;
}
/**
 * A refreshed readability block (ourteam-frontend#3831, ourteam-backend#4995).
 *
 * The same four facts the audit carries, returned on its own so the card can
 * re-render without a rescan. `measuredAt` is what makes the number readable
 * next to an older scan: a score that disagrees with the scan beside it looks
 * like a bug unless the card can say it was measured later.
 */
/**
 * What the crawl found out about itself (ourteam-backend#4995 follow-up).
 *
 * A null score with `crawl_blocked` says "too few pages could be read" and
 * leaves the operator unable to tell 2 readable pages of 30 from 20 of 30 —
 * two situations with completely different next moves. `readableFloor` travels
 * with the counts because the verdict is meaningless without it: 20 of 30 is
 * blocked at a 70% floor and fine at 60%.
 */
export interface AeoReadabilityCrawl {
    pagesCrawled: number;
    readablePages: number;
    /** `null` for an empty crawl: no pages read is not a share of zero. */
    readableShare?: number | null;
    readableFloor?: number | null;
    crawlBlocked?: boolean;
    shellUnrendered?: boolean;
}

export interface AeoReadabilityBlock {
    score: number | null;
    verdict?: string | null;
    /** The same vocabulary as `aiReadabilityAbsent`. Null when scored. */
    reason?: AeoReadabilityAbsence | null;
    components?: AeoReadabilityComponent[];
    measuredAt?: string | null;
    crawl?: AeoReadabilityCrawl | null;
}

/**
 * `recompute` reads the checks the stored results already carry and answers at
 * once; `crawl` has to read the site again because those checks predate the
 * score. The difference is a minute of waiting, so the card says which.
 */
export type AeoReadabilityRefreshMode = "recompute" | "crawl";

export interface AeoReadabilityRefreshJob {
    status: "queued" | "running" | "done" | "failed";
    mode: AeoReadabilityRefreshMode;
    /** The backend's own sentence on a failure, rendered verbatim. */
    error?: string | null;
    aiReadability?: AeoReadabilityBlock | null;
}

/**
 * Starting one.
 *
 * `busy` is the 409: an assessment for this business is already running, and
 * two crawls of one site at once is the thing the backend refuses. `absent` is
 * a 404 — the route is not deployed here, and the control hides itself rather
 * than offering an action this deployment cannot perform.
 */
export type AeoReadabilityRefreshStart =
    | {
        ok: true;
        jobId: string;
        mode: AeoReadabilityRefreshMode;
        assessmentId?: string | null;
        /** Present on `recompute`, which finishes inside the POST. Rendering it
         *  straight away saves a poll the answer is already past. */
        aiReadability?: AeoReadabilityBlock | null;
    }
    | { ok: false; kind: "busy"; message: string; assessmentId?: string | null }
    | { ok: false; kind: "absent" }
    | { ok: false; kind: "error"; message: string };


export interface AeoBusinessAudit {
    /** Technical hygiene — schema, meta, broken links, thin content. NOT search
     *  performance. The BE dropped the `seoScore`/`seoVerdict` aliases that made
     *  it look like one (ourteam-backend#3818); real search numbers live in
     *  `results.searchPerformance`, measured from Search Console. */
    siteHealthScore: number;
    /** Why `siteHealthScore` is absent, when it is (`build_business_audit`):
     *  `crawl_failed` the crawler did not run to completion · `crawl_blocked`
     *  it ran and every page it fetched was unreadable (a WAF or rate limit) ·
     *  `not_crawled` it ran and produced nothing to score. `null` = scored. */
    siteHealthScoreAbsent?: AeoCrawlAbsence | null;
    /** How many crawled pages were readable. Absent on older runs. */
    siteHealthReadablePages?: number | null;
    /**
     * The third gauge (ourteam-backend#4877, B6): can an assistant READ the
     * site, as opposed to is the site technically sound. The two disagree by 70
     * points on the site that prompted it, so they are two numbers and never
     * one blend — Site Health's value and formula are untouched.
     *
     * The whole group is absent on a run finalized before it shipped, which is
     * every stored scan today: `businessAudit` is written once at finalize.
     * A `null` score with a reason is a real answer and must never render as 0.
     */
    aiReadabilityScore?: number | null;
    aiReadabilityVerdict?: string | null;
    aiReadabilityAbsent?: AeoReadabilityAbsence | null;
    /** `[]` whenever the score is null. The BE drops a component with nothing
     *  to cover rather than scoring it zero, so this list — and the mean's
     *  denominator — differ between businesses. */
    aiReadabilityComponents?: AeoReadabilityComponent[];
    /**
     * When the readability block was measured, when that is NOT the scan
     * (ourteam-backend#4995). Absent on every stored scan, and absent means the
     * scan's own date — never "unknown", and never a reason to hide the score.
     */
    aiReadabilityMeasuredAt?: string | null;
    /** The crawl behind the readability block — `businessAudit.aiReadabilityCrawl`.
     *  Absent on a scan finalized before it shipped, and absent means unknown. */
    aiReadabilityCrawl?: AeoReadabilityCrawl | null;
    /** Why `aeoScore` is absent, when it is (#4096): the same codes as `aiVisibility.scoreAbsent`. */
    aeoScoreAbsent?: string | null;
    /** `null` when the scan produced no score; `aeoScoreAbsent` says why. Never 0 for unknown. */
    aeoScore: number | null;
    siteHealthVerdict: string;
    aeoVerdict: string;
    strengths: string[];
    weaknesses: string[];
    competitors: AeoBusinessCompetitor[];
    /** All three are always sent by `build_business_audit()`. They were once
     *  believed to be missing and were hard-coded empty by the adapter, which
     *  rendered real assessments as blank states. */
    priorityPages: AeoPriorityPage[];
    underperforming: AeoUnderperformingPage[];
    recommendations: AeoRecommendation[];
    /** Optional because the BE omits the key when there is no GSC connection —
     *  an absent block means "not measured", never zero traffic. */
    traffic?: AeoTraffic;
    /** GA4 is connected and was read, and recorded NO sessions in this window
     *  (ourteam-backend#6749). Only ever without `traffic`. Absent on an older BE. */
    trafficEmpty?: { window?: { start?: string; end?: string; days?: number } } | null;
}

/**
 * A page's cross-run state inside a check (#4350/#4351).
 *
 * `open` | `new` | `regressed` are FAILING right now. `fixed` | `verified` were
 * failing and are not any more. `not_crawled` was failing and this run did not
 * re-read the page, so we know nothing new about it.
 *
 * **Absent means failing.** A block stored before run-identity existed has no
 * status, and an entry in `pages[]` meant "this page has this problem" back
 * then — so undefined must be read as a live finding, never dropped. Mirrors
 * `page_is_failing()` in app/dependencies/aeo_audit.py.
 */
export type AeoAuditPageStatus =
    | "open" | "new" | "regressed"
    | "fixed" | "verified" | "not_crawled";

/** An affected page inside `results.audit.checks[].pages[]` (#3681). */
export interface AeoAuditCheckPage {
    url: string;
    title?: string;
    discovered?: string;
    meta?: string;
    /**
     * Whether this page still has the problem (#4350). Once a business is
     * re-scanned, `pages[]` KEEPS the pages that were fixed — that is the point,
     * it is how "fixed" becomes reportable instead of a row silently vanishing —
     * and `count` narrows to mean only the failing ones. A consumer that renders
     * the whole array against that count states two different numbers for the
     * same thing.
     */
    status?: AeoAuditPageStatus;
    /** When the finding was FIRST reported. Carried across runs, never re-dated
     *  (`discovered` is aliased to it). */
    firstSeen?: string;
    /** The last run that observed this page failing. */
    lastSeen?: string;
    /** Set when the page first turned fixed/verified. */
    resolvedAt?: string;
    /**
     * What an OPERATOR asserts about this page and this check (#4530 §3) —
     * a fix made by hand in WordPress, outside Oura, which had no way of being
     * recorded and so stayed open on every surface until the next crawl.
     *
     * Stored beside the measurement, never on top of it. `status` above is what
     * the crawl found; this is what a person said. `null` means nobody has said
     * anything, which is different from `not_started`.
     */
    operatorStatus?: "not_started" | "in_progress" | "done" | null;
    operatorStatusAt?: string | null;
    operatorStatusBy?: string | null;
    /**
     * The two answers disagree: marked done, and the last crawl still flags it.
     *
     * Reported rather than resolved, on purpose. Reverting the claim calls the
     * operator wrong; keeping it calls the crawl wrong; only one of those is
     * checkable afterwards. So both are kept and the UI says so.
     */
    statusDisagreement?: "still_failing" | null;
    /**
     * WHERE on the page the problem is, for checks no draft can fix (#4529 §2).
     * Present when the parent check has `locates: true`: the images with no alt
     * (`{element: "img", src}`) or the anchors carrying a dead link
     * (`{element: "a", text, href}`).
     */
    locations?: AeoAuditPageLocation[];
    /** `broken_outbound` only (#4514): the destinations that no longer resolve,
     *  with the words on the page that link to each. */
    deadLinks?: AeoDeadLink[];
}

/** One offending element on an affected page (#4529 §2). */
export interface AeoAuditPageLocation {
    element: "img" | "a" | string;
    /** `img` — the image source. */
    src?: string | null;
    /** `a` — the anchor text, and where it points. */
    text?: string | null;
    href?: string | null;
}

/**
 * A dead outbound link (#4514).
 *
 * Only destinations that answered 404 or 410, could not be reached at all, or
 * now redirect to the destination's home page are counted. Anything ambiguous —
 * a 403 from a publisher that blocks bots, anything that timed out — is left
 * out rather than reported as broken, so a row here is worth acting on.
 */
export interface AeoDeadLink {
    url: string;
    reason?: string | null;
    status?: number | null;
    finalUrl?: string | null;
    /** The words on the page that carry the link. */
    anchor?: string | null;
}

/** One entry of `results.audit.checks[]` — a site-health finding (#3681). */
export interface AeoAuditCheck {
    key: string;
    label: string;
    severity: string;
    count: number;
    desc: string;
    /** "About the issue" copy shown in the fix drawer. */
    about?: string;
    /** Remediation steps shown in the fix drawer. */
    fix?: string[];
    /** Header for the issue-specific page column. */
    pageColLabel?: string;
    /** The actual affected pages — the BE DOES send these; an earlier version of
     *  the adapter dropped them, which is why the issues table showed a count
     *  with no URLs behind it.
     *
     *  On a re-scanned business this list is WIDER than `count`: it also carries
     *  the pages this issue no longer affects. Filter with `isFailingPage`
     *  before showing it as a worklist. */
    pages?: AeoAuditCheckPage[];
    /**
     * What a draft can actually fix for THIS finding (#4529 §1).
     *
     * Every issue used to offer the same four types whatever the issue was, so
     * Core Web Vitals offered to write an FAQ block and "Links to pages that no
     * longer exist" offered a meta description. Neither does anything about the
     * finding.
     *
     * An EMPTY ARRAY is the answer, not an omission: no draft resolves a
     * redirect chain, a canonical, a missing robots.txt or a slow page, so the
     * drawer shows no draft control at all. Absent (not `[]`) means an older
     * payload that predates the field — fall back, do not read it as empty.
     */
    draftTypes?: string[];
    /** True when the useful answer is WHERE on the page the problem is, which
     *  the affected pages carry in `locations[]` instead of a draft (#4529 §2). */
    locates?: boolean;
    /** The decomposition of `pages[]` by state (#4350). `count` is
     *  open + new + regressed; this is where the rest of the story lives. */
    counts?: {
        open: number;
        new: number;
        regressed: number;
        fixed: number;
        verified: number;
        notCrawled: number;
    };
}

/**
 * Does this page still have the problem?
 *
 * The one place the rule lives, so every surface agrees. `undefined` counts as
 * failing — see `AeoAuditPageStatus`.
 */
export function isFailingPage(page: { status?: string }): boolean {
    return page.status === undefined
        || page.status === "open"
        || page.status === "new"
        || page.status === "regressed";
}

/**
 * One of the ~50 generated customer questions — `results.queries[]`, produced by
 * `generate_queries()` (#3621) and stored on every run. `bucket` is the query's
 * intent class from the anchored recipe.
 */
/** One correction of a question's wording (ourteam-backend#6249 adds `version` and `history`). */
export interface AeoQuestionCorrected {
    version?: number;
    at: string;
    by?: string;
    was?: string;
    history?: Array<{ version: number; at: string; by?: string; was: string; text: string }>;
}

/** A correction as it runs: clearing → asking → asked, or failed / clear_failed. */
/**
 * Staff corrected one engine's verdict on one question (Jack, 22 Oct 2026):
 * Oura read the answer as Named when it was not, or the other way. Nothing is
 * asked again; the score is recounted with the correction. Staff-only on read.
 */
export interface AeoVerdictCorrection {
    named: boolean;
    /** What Oura read before the correction. */
    was: boolean;
    reason: AeoVerdictReason;
    note?: string | null;
    by?: string | null;
    at?: string | null;
}

export type AeoVerdictReason = "other_business" | "area_name" | "old_branch" | "missed_name" | "other";

/** The engines a verdict correction names, as the route takes them. */
export type AeoVerdictEngine = "chatgpt" | "gemini" | "google_ai";

export interface AeoQuestionCorrection {
    state: "clearing" | "asking" | "asked" | "failed" | "clear_failed";
    at?: string;
    by?: string;
    was?: string;
    text?: string;
    version?: number;
}

export interface AeoQuery {
    text: string;
    /**
     * The v3 slot group this question was built from — `anchor`, `outlets`,
     * `adjacent`, `core_service`, `price`, `category_benefit`, and eight more
     * (`groups` in aeo_visibility_spec.json).
     *
     * `bucket` is the SAME value under an older name: aeo_query_spec.py writes
     * both from `group`, and kept `bucket` only so `branded_query_texts()`
     * keeps its shape. Read `check` for grouping, not either of these — there
     * are thirteen groups and the screen is not thirteen sections deep.
     */
    bucket: string;
    group?: string;
    /**
     * Which of the four checks this question measures (#3538). The v3 spec
     * carries it on every question precisely so the result can be reported per
     * check instead of as one blended number.
     *
     * Optional: a pre-v3 set has no checks, and that is a different statement
     * from "every check scored zero".
     */
    check?: AeoVisibilityCheckName;
    /** The value the slot was filled from — an area, a service, an audience.
     *  What makes it possible to defend a specific question to a client. */
    fill?: string;
    /** Slot number, 1-50. `n` and `slot_n` carry the same value. */
    n?: number;
    slot_n?: number;
    /**
     * Staff corrected this question's wording on the finished scan and asked it again
     * (POST /assessments/{id}/questions/{n}/correct). `version`: 1 + the corrections so far,
     * the original wording being V1 (ourteam-backend#6249). Absent on an older backend: read
     * a present `corrected` as edited, with no number.
     */
    corrected?: AeoQuestionCorrected | null;
    language: string;
}

/** The four checks of the v3 Visibility Set, in the spec's own order — 26, 12,
 *  8 and 4 questions respectively. */
export type AeoVisibilityCheckName =
    | "location" | "specialty" | "intent" | "authority"
    // A brand scan (Visibility Set v4.5, ourteam-backend#5883) carries these three instead.
    | "pick" | "source" | "supplier";

/**
 * One of `results.aiVisibility.checks` — `check_scores()` in aeo_visibility.py.
 *
 * Reported as a COUNT and never a percentage: the authority check is four
 * questions, and "1 of 4" is honest where "25%" implies a precision four
 * questions cannot carry. `label` is the string the BE already formatted for
 * exactly this reason — render it rather than recomputing from the numbers.
 */
export interface AeoVisibilityCheck {
    mentioned: number;
    /** Errored and unanswered probes are excluded from the denominator, so this
     *  can be lower than the spec's slot count. `0` with `label: "not measured"`
     *  when nothing in this check was answered. */
    answered: number;
    /** `"8 of 26 answers"` (`"8 of 26"` before #4934), or `"not measured"`.
     *  Pre-formatted by the BE. */
    label: string;
    /** `"answers"` from ourteam-backend#4934 on — and `label` then already
     *  carries it ("5 of 28 answers"). Absent on older scans, whose label is a
     *  bare "5 of 28". */
    unit?: "answers";
    /** Question counts for the SAME check (#4934), the unit a group header
     *  shows. `label` counts answers (one per engine per question); these count
     *  questions. Absent on older scans. */
    questions?: number;
    questionsAnswered?: number;
    questionsNamed?: number;
    /** Questions no scored answer came from — unwinnable, or every answer
     *  withheld. Said out loud so the gap reads as rigour, not as missing rows. */
    questionsExcluded?: number;
    /** What this check asks, in the client's words. */
    question: string;
    /** The work that closes this specific gap — four checks, four prices. */
    fix: string;
}

/** One `aiVisibility.sourceDomains[]` row. */
export interface AeoSourceDomain {
    domain: string;
    /** Answers citing this site — not pages, not URLs. */
    answers: number;
    /** This is the client's own site. */
    own: boolean;
    /**
     * The slot `n` of every question counted in `answers`, so a source row can
     * list the questions behind its count. Absent means not listed, never
     * "none". BE handoff: "Source rows: questions and kind".
     */
    questions?: number[] | null;
    /** What kind of site it is. Absent means unclassified. Same handoff. */
    kind?: AeoSourceKind | null;
}

/** The kinds a source domain can be classified as. */
export type AeoSourceKind = "map" | "directory" | "review" | "media" | "social" | "own" | "rival" | "other";

/** `aiVisibility.coverage` (#4354) — what the score was divided by. */
export interface AeoVisibilityCoverage {
    /** Questions the score refused to divide by: unwinnable slots on a frozen
     *  v3 set, and branded questions. Both absent on older runs. */
    headlineExcluded?: number;
    brandedExcluded?: number;
    enginesProbed: number;
    enginesTotal: number;
    questionsAsked: number;
    questionsAnswered: number;
    /** The score's actual denominator: answered, non-withheld answers. */
    answersScored: number;
    /** The question-set recipe, e.g. `"visibility-set-v4.2"`. `null` when unknown. */
    recipe: string | null;
    crossChecked?: boolean;
}

/**
 * Which fact held the 1-to-5 maturity level below what the area scores alone
 * give (`maturity_from_areas` in aeo_report_metrics.py). `null` = not capped.
 */
export type AeoLevelCap = "site_health" | "rented_visibility";

/** `results.aiVisibility.engines[]` (#3684) — `_engine_shares()` in aeo_visibility.py. */
export interface AeoEngineShare {
    /** Which instrument produced this engine's row (`chatgpt_app`, `openai_api`, `api`). */
    instrument?: string;
    isChatgptApp?: boolean;
    /** Kept for its links only when the app answered the same questions. */
    citationOnly?: boolean;
    /** False when the engine was never asked; `summary` then says why. Google AI
     *  ships `probed: false` with `skipReason: "not_supported"` today. */
    probed?: boolean;
    skipReason?: string;
    skipDetail?: string;
    engine: string;
    mentions: number;
    sharePct: number;
    summary: string;
    rivals: string[];
    /**
     * How many answers the engine actually produced, when that differs from the
     * question count (ourteam-frontend#3922). Google renders an AI Overview for
     * only some searches, so its hit rate is over THIS, never over 50.
     */
    answered?: number | null;
    /** `"engine_hit_rate"` says `sharePct` is mentions over `answered`. */
    sharePctBasis?: string | null;
    /**
     * What the engine was asked and what came back
     * (ourteam-backend#5037/#5067).
     *
     * `answered + notShown + failed === asked`, ALWAYS. The three are different
     * facts and the screen must keep them apart: `notShown` is a fact about
     * Google, `failed` is a fact about our read, and neither is a zero about
     * the business.
     */
    coverage?: {
        asked: number;
        answered: number;
        /** Google rendered no AI Overview for the search. */
        notShown: number;
        /** We could not read the page. NOT a measurement. */
        failed: number;
        /** Still being read by the backend's re-read (ourteam-backend#5619).
         *  Absent when 0. Never counted in `failed`, never "not named". */
        pending?: number;
    } | null;
    /** When THIS row was read, which a refresh moves without moving the scan. */
    capturedAt?: string | null;
    country?: string | null;
}

/** `results.aiVisibility.countries[]` (#3684) — `_country_shares()`. */
export interface AeoCountryShare {
    country: string;
    flag: string;
    mentions: number;
    sharePct: number;
}

/**
 * `results.aiVisibility` (#3684) — built specifically to match
 * `src/oura/audit.ts`'s `AiVisibility` mock output field-for-field (see the
 * header comment in app/dependencies/aeo_visibility.py, which names that file
 * and Questions.tsx as the intended consumer).
 */
export interface AeoAiVisibility {
    /** Why `score` is absent, when it is: `not_probed` (no engine was asked) or
     *  `insufficient_sample` (too few answers to score). Never a bare null. */
    scoreAbsent?: string | null;
    /** Per engine, as counts (D5): the verdict keys off the weakest engine while
     *  `score` stays the pooled number. Absent on runs before it shipped. */
    engineRates?: Record<string, { mentioned: number; answered: number }>;
    /** Questions on which at least one engine named the business, counted once
     *  each (#4976). ABSENT when no row answered, never 0: the FE then shows
     *  `scoreAbsent`'s reason instead of a count. */
    questionsNamed?: number;
    /**
     * What `questionsNamed` is out of: the headline questions at least one
     * engine gave a scored answer to. Not sent yet; the FE derives it from the
     * coverage block and the probes meanwhile. BE handoff: "Named-count
     * denominator".
     */
    questionsOf?: number;
    /**
     * Which engines `questionsNamed` counted (ourteam-backend#5411 BE-5).
     *
     *   "all_engines" — every engine the scan measured, Google's AI included
     *   "assistants"  — ChatGPT and Gemini only, the rule before BE-5
     *   absent        — a scan stored before the field existed, so: assistants
     *
     * IT IS PER SCAN, not per deployment. Old scans keep their stored numbers
     * until they are re-scanned, so the frontend must count the way THIS scan
     * was counted or the headline and its own table disagree (#4025 FE-5).
     */
    countingRule?: "all_engines" | "assistants" | null;
    minEngineVisibility?: number | null;
    engineSplit?: number;
    splitNote?: string | null;
    /** `null` under the sample floor or when no engine was asked; `scoreAbsent` says why. */
    score: number | null;
    verdict: string;
    blurb: string;
    capturedAt: string;
    mentions: number;
    /** `null` on a first scan, or when the prior was 0 (#4934). */
    mentionsDeltaPct: number | null;
    /**
     * The measured own-site citation share (ourteam-backend#4868).
     *
     * THE ONLY HONEST SOURCE FOR THIS CARD. `citations` counts answers that
     * cited the CLIENT, and `ownDomainAnswers` counts the same thing, so
     * dividing one by the other is 100% on every business that has any, and
     * `null` — which renders "not measured" — on every business that has none.
     * The Lush Clinic read "25 of 25, 100%" above a source list where two other
     * domains outranked it; Luxe Dental read "Not measured" over a measured
     * zero (#3845).
     *
     * `denominator` is answers from the engines that show sources at all, so it
     * is 50 on a Gemini-only scan and 100 when both answered. A zero here is a
     * measurement, not an absence.
     */
    citationShare?: {
        count: number;
        denominator: number;
        /** 0..1, the backend's own division. */
        share: number;
        /** Non-null ⇒ nothing was measured, and this says why. */
        absent?: string | null;
        /** Which engines the figure covers, e.g. `["gemini", "openai"]`. */
        engines?: string[] | null;
        /**
         * WHAT THE COUNT COUNTS, and therefore the word the screen prints:
         * `"questions_own_domain_cited"` counts questions of the set,
         * `"discovery_answers_own_domain_cited"` counts answers
         * (ourteam-backend#5029/#5030). Absent on older scans, which counted
         * answers. Never assume — read it.
         */
        basis?: string | null;
    } | null;
    /** `null` when the instrument cannot see citations — see `citationsAbsent`. */
    citations: number | null;
    citationsDeltaPct: number | null;
    /**
     * Why `citations` is null, when it is: `"instrument_renders_no_citations"`
     * (no engine on the scan returned a source). Never a bare null to
     * interpret.
     *
     * PRESENT-AND-NULL IS A MEASUREMENT. The key missing entirely is a scan
     * from before the backend sent it, and only that falls back to
     * `grounding.citationsMeasurable` (#3889).
     */
    citationsAbsent?: string | null;
    /** Which engines the citation figure was read from, e.g. `["gemini"]`
     *  (ourteam-backend#5013/#5014). Absent on older scans. */
    citationsFrom?: string[] | null;
    /** The unit `sourceDomains[].answers` is counted in: `"questions"` since
     *  ourteam-backend#5029. Absent ⇒ answers, as it always was. The key stays
     *  `answers` for compatibility; its VALUE is a question count. */
    sourceDomainsBasis?: string | null;
    citedPages: number;
    citedPagesDeltaPct: number | null;
    /** How much the score is based on (#4354). Absent on older runs. */
    coverage?: AeoVisibilityCoverage;
    /** The sites the answers quote, ranked by how many ANSWERS cite each —
     *  top 20 (`source_domains`, ourteam-backend#4934). Replaces the
     *  `citedPages` volume count on the card. Absent on older scans. */
    sourceDomains?: AeoSourceDomain[];
    /** Distinct sites across all answers, beyond the 20 listed. */
    sourceDomainsTotal?: number;
    /** Answers citing the client's own site. `null` = not computed, never 0. */
    ownDomainAnswers?: number | null;
    trend: number[];
    /**
     * What ONE point of `trend` means (#4354). `"per_assessment"` — a point is
     * a scan, not a week. Optional: runs finalized before #4354 have no basis
     * and the FE must not assume weeks.
     */
    trendBasis?: string;
    /**
     * When each `trend` point was captured, **index-aligned with `trend`**.
     * Entries can be `null` on points stored before #4354 backfilled dates.
     *
     * This is what makes a trend break locatable (#3539): `queries_trend_break`
     * carries a timestamp, and only these dates say which point it falls after.
     */
    trendCapturedAt?: (string | null)[];
    engines: AeoEngineShare[];
    countries: AeoCountryShare[];
    /**
     * The four checks, each as a count (#3538) — `check_scores()`.
     *
     * **Absent, not zeroed, on a pre-v3 scan**, which is deliberate on the BE
     * side: it lets the FE say "this set predates the four checks" instead of
     * showing four zeros that read as four failures.
     */
    checks?: Partial<Record<AeoVisibilityCheckName, AeoVisibilityCheck>>;
    /** Which questions each rival was named on (#3803). Only computable now
     *  that probes carry their `query`; absent on older runs. */
    competitorWins?: AeoCompetitorWin[];
}

/** `results.pageSpeed.runs[].opportunities[]` (#3682) — `_opportunities()`. */
export interface AeoPsOpportunity {
    label: string;
    savingMs: number;
    /** Lighthouse's audit id (ourteam-backend#4491), stable where `label` is
     *  not: Google renames titles between Lighthouse versions. Absent on runs
     *  stored before it was sent. */
    id?: string;
}

/** One entry of `results.pageSpeed.runs[]` — `_parse_run()` in aeo_pagespeed.py.
 *  Field names match `PageSpeedRun` in src/oura/audit.ts exactly. */
export interface AeoPageSpeedRun {
    strategy: "mobile" | "desktop";
    /** Nullable on every numeric metric: `_parse_run()` returns 0 when PSI did
     *  not supply a value, and a fabricated 0 is indistinguishable from a real
     *  measurement — `inpMs: 0` rendered as a green "0 ms" core vital on a site
     *  CrUX had no INP samples for. The BE fix to send null is handed off; these
     *  are typed nullable now so the FE is already correct when it lands and
     *  degrades honestly in the meantime. */
    performance: number | null;
    lcpMs: number | null;
    fcpMs: number | null;
    cls: number | null;
    tbtMs: number | null;
    siMs: number | null;
    /** Field (CrUX), not lab — Lighthouse does not measure INP. */
    inpMs: number | null;
    ttfbMs: number | null;
    ttiMs: number | null;
    fieldData: "good" | "needs-improvement" | "poor" | "insufficient";
    fieldPass: boolean;
    opportunities: AeoPsOpportunity[];
    /** Accessibility / Best Practices / SEO — the other three Lighthouse
     *  categories. `_psi_call()` has requested all four since #3957 and
     *  `_category_score()` returns them (null, never 0, when absent), but this
     *  type never declared them so the FE discarded all three. */
    accessibility?: number | null;
    bestPractices?: number | null;
    seo?: number | null;
    /** Lighthouse's `final-screenshot` audit — a `data:image/jpeg;base64,…` URI
     *  of the page as actually rendered at this strategy's viewport. The BE has
     *  captured it since #3682 (`aeo_pagespeed.py` `_parse_run`); this type
     *  omitted it, so it was dropped on the floor and the device panel drew a
     *  hardcoded mock instead. Optional because a PSI run can succeed without
     *  the audit (rare, but it is not a guaranteed field). */
    screenshot?: string | null;
    /** When THIS strategy was measured (ourteam-backend#4470). A refresh where
     *  one strategy failed keeps that strategy's last good run, so the block's
     *  single `capturedAt` presented a week-old desktop run as "just now".
     *  Optional — a payload stored before #4470 has no per-run stamp. */
    capturedAt?: string | null;
    /** How many PSI calls this run is the median of (`_sampled_run`, #4492).
     *  The homepage runs are the middle of 3; absent on older payloads. */
    samples?: number;
    /** Failed opportunities before `opportunities[]` was truncated (#4491),
     *  so a card can say "top 3 of 11". Absent on older payloads. */
    opportunitiesTotal?: number;
}

/** `results.pageSpeed` (#3682) — `run_pagespeed()`. `null`/absent when every
 *  PSI call failed (the BE omits the block rather than fabricate). */
export interface AeoPageSpeed {
    capturedAt: string;
    runs: AeoPageSpeedRun[];
    /** Built from `_parse_run()` per page, so both numbers became nullable with
     *  ourteam-backend#4470 — `null` when PSI could not score that page, never
     *  a fabricated 0. */
    pages: { url: string; title: string; performance: number | null; lcpMs: number | null }[];
    /** When `pages[]` was captured (ourteam-backend#4470) — Re-scan does not
     *  re-test them, so they carry their own stamp. Optional on older payloads. */
    pagesCapturedAt?: string | null;
    /** Key pages the business has before the list was cut to the ones tested
     *  (#4491). Absent on older payloads. */
    pagesTotal?: number;
}

// ─── results.searchPerformance (ourteam-backend#3818) ────────────────────────
// Real search numbers from Search Console, replacing the crawl-hygiene figure
// that used to be labelled "SEO score". Every value is a metric Google returned
// or arithmetic over it — nothing here is model-generated.

export interface AeoSearchWindow {
    start: string;
    end: string;
    days: number;
}

export interface AeoSearchTotals {
    clicks: number;
    impressions: number;
    ctr: number;
    /** Impression-weighted, so a query seen 5,000 times counts for more than one
     *  seen twice. `null` when there were no impressions at all. */
    avgPosition: number | null;
    /** `null`, never 0, when there is no previous window to compare against —
     *  a first scan has no movement, and 0 would read as "flat", which is a
     *  measurement we did not make. */
    clicksDeltaPct: number | null;
    impressionsDeltaPct: number | null;
}

export interface AeoSearchKeyword {
    query: string;
    clicks: number;
    impressions: number;
    ctr: number;
    position: number;
    /** Contains a token from the business name. Ranking first for your own name
     *  is table stakes, so the score measures position on non-branded only. */
    branded: boolean;
    /** NEGATIVE is an improvement (position 8 → 4 is -4). `null` when the query
     *  is new this window: it has not moved, it has appeared. */
    positionDelta: number | null;
}

export interface AeoSearchPage {
    url: string;
    clicks: number;
    impressions: number;
    ctr: number;
    position: number;
}

/** Keyword counts by ranking band. **Cumulative and overlapping by design**
 *  (top3 ⊂ top10 ⊂ top20), and counted over NON-BRANDED keywords only — so
 *  these do not sum to `keywords.length`, and must never be stacked as if they
 *  were mutually exclusive segments. */
export interface AeoSearchBuckets {
    top3: number;
    top10: number;
    top20: number;
    beyond20: number;
}

export interface AeoSearchRollup {
    clicks: number;
    impressions: number;
    ctr: number;
}

/** `results.searchPerformance`. When Search Console is not connected — or the
 *  connection has not synced since #3818 shipped — the BE returns only
 *  `{connected: false, searchScore: null}`, so every other field is optional. */
export interface AeoSearchPerformance {
    connected: boolean;
    /** 0–100, or `null` when not connected. Never 0 as a stand-in: a zero would
     *  read as measured-and-terrible, which is a different claim. */
    searchScore: number | null;
    window?: AeoSearchWindow;
    previousWindow?: AeoSearchWindow;
    totals?: AeoSearchTotals;
    buckets?: AeoSearchBuckets;
    branded?: AeoSearchRollup;
    nonBranded?: AeoSearchRollup;
    keywords?: AeoSearchKeyword[];
    pages?: AeoSearchPage[];
    pagesWithImpressions?: number;
    pagesCrawled?: number | null;
    fetchedAt?: string;
    /** The saved AI summary for this reader (ourteam-backend#6426). Null or
     *  absent when none passed the backend's checks: the screen keeps its own
     *  template line then. */
    monthSummary?: AeoMonthSummary | null;
    /** Clicks and impressions per ISO week for 12 months. A week Google sent no
     *  row for is left out, never zero-filled. */
    weekly?: { weekStart: string; clicks: number; impressions: number }[];
}

/** `searchPerformance.monthSummary`: every line is written server-side from
 *  the figures, never free text from the model. `writeUp` is client-only. */
export interface AeoMonthSummary {
    line1: string;
    line2: string | null;
    writeUp: { happened: string; why: string; worthKnowing: string } | null;
    generatedAt: string | null;
}

// ─── results.probes (ourteam-backend#3803) ───────────────────────────────────

/** One engine answer to one question. `query` is what makes a probe joinable
 *  back to the question that produced it — before #3803 probes carried no query
 *  and (as that ticket discovered) were empty on every real run. */
/**
 * One question's Google AI Overview reading (ourteam-backend#5123).
 *
 * Kept OUT of `probes[]` on purpose: a probe is an engine we asked, and Google
 * is not asked — it either renders an overview on the results page or it does
 * not. So the row has to carry two outcomes a probe has no way to say, and
 * NEITHER OF THEM IS A MISS:
 *
 *   `shown: false`  Google rendered no overview for this question. A fact
 *                   about Google, not about the business.
 *   `shown: null`   the read itself failed (`error` says how). Not measured.
 *
 * Counting either as "not named" is the bug this block exists to prevent, and
 * the backend's own `coverage` splits them out for the same reason:
 * `answered` + `notShown` + `failed` = `asked`.
 *
 * Absent (not `[]`) on a scan from before #5123, or where Google AI was never
 * attempted — so absence still means "not measured", as everywhere else.
 */
export interface AeoGoogleAiReading {
    /** The question's slot number: the join key onto `queries[]`. */
    n: number;
    query: string;
    /** `"google_ai"`. */
    engine: string;
    /** `"google_serp"`. */
    instrument: string;
    /** Whether Google rendered an AI Overview. `null` = the read failed. */
    shown: boolean | null;
    /** Whether that overview named the business. `null` whenever `shown` is not `true`. */
    mentioned: boolean | null;
    /** Raw listing titles the overview named. */
    rivals: string[];
    /** The same rivals merged to the competitors table's names. */
    rivalNames?: string[];
    /** The domains the overview cited. */
    sources?: string[];
    /** The overview text, trimmed to 600 chars by the backend. */
    answer: string;
    answer_truncated?: boolean;
    /** Why the read failed, when it did. */
    error: string | null;
    /** `true` on a page still being read (ourteam-backend#5619). Then `shown`
     *  and `mentioned` are `null`. Absent on every other row. */
    pending?: boolean;
}

/**
 * `results.googleAiPending` (ourteam-backend#5619). Present only while pages
 * are still being read; absent otherwise, which means "nothing pending", not
 * "not known".
 */
export interface AeoGoogleAiPending {
    /** Pages still being read. */
    pages: number;
    /** Pages the Google read covers. */
    of: number;
    /** `true`: the re-read is running now. `false` on a finished scan: it
     *  stopped, staff should refresh. `null`: could not be told. */
    rereading: boolean | null;
}

export interface AeoProbe {
    engine: string;
    query: string;
    query_id: string;
    country: string;
    mentioned: boolean;
    cited: boolean;
    cited_pages: string[];
    answer: string;
    /** Truncated to 600 chars by the BE; full text stays in the engine store. */
    answer_truncated?: boolean;
    /** Raw listing titles as the answer named them — one chain can appear as
     *  several branches ("Uroot Spa Chinatown (Petaling Street)"). */
    rivals: string[];
    /** The same rivals merged to the name the competitors table uses
     *  (`rival_display_names`, ourteam-backend#4934). Absent on scans finalized
     *  before it shipped — read `rivals` then. */
    rivalNames?: string[];
    /** Which instrument asked this one question (`"openai"`, `"chatgpt_app"`…).
     *  A run can mix instruments, so this is per probe. Absent on engines
     *  that have only one way of being asked. */
    instrument?: string | null;
    /** The engine as the run labels it, e.g. `"OpenAI (API)"`. */
    engineLabel?: string | null;
    sentiment?: string | null;
    sentiment_score?: number | null;
    /**
     * Prose came back, but the relevance check found it was not an answer to
     * the question (a sign-in wall, another conversation). The backend drops
     * the row from every numerator and denominator (`answered()` in
     * aeo_visibility.py); a screen that counted it would read it as "not
     * named". Already on the wire, untyped until now.
     */
    withheld?: boolean | null;
}

/** `aiVisibility.competitorWins[]` — which questions each rival was named on.
 *  "Appears on", not "beats us on": both can be named in one answer. */
export interface AeoCompetitorWin {
    name: string;
    queryCount: number;
    queries: string[];
    /** Every listing title folded into `name` (#4864), so a probe that kept
     *  the raw title still joins to this rival. */
    aliases?: string[];
    /**
     * What the rival has that the business does not, from a verified pass over
     * the answers, and the slots it rests on. Optional; absent means no reason
     * was established, and the screen then says nothing rather than guessing.
     * BE handoff: "Rival gap sentence".
     */
    gap?: { text: string; questions?: number[] } | null;
    /**
     * The questions the rival was named on and the business was not, over the
     * verdict answers only (the ChatGPT app's row where it ran, withheld rows
     * dropped), in the same form as `queries`. `queryCount` counts every
     * question the rival appears on, the client's own included, so it cannot
     * say "takes N". Optional; absent means derive it from the probes.
     * BE handoff: "Rivals: the questions where a rival was named instead".
     */
    insteadCount?: number;
    insteadQueries?: string[];
    /** Why `gap` is null. Same handoff. */
    gapAbsent?: "no_instead_questions" | "outside_top_rivals" | "no_supported_reason" | "generation_failed" | null;
}

// ─── results report metrics (ourteam-backend#3802) ───────────────────────────

export interface AeoAreaScore {
    key: string;
    label: string;
    /** 0–10, or `null` when this area could not be measured on this run. */
    score_10: number | null;
    level_1_to_5: number | null;
    measured: boolean;
}

export interface AeoSentiment {
    /** 0–100 across the answers that mentioned the business. */
    score: number;
    mentionsScored: number;
    breakdown: { positive: number; neutral: number; negative: number };
}

/** `covered` → Answers it · `partial` → Thin · `missing` → No page
 *  (`_coverage_status` in aeo_report_metrics.py, ourteam-backend#4937). */
export type AeoCoverageStatus = "covered" | "partial" | "missing";

export interface AeoCoverageGap {
    query: string;
    similarity: number;
    page: string;
    answered: boolean;
    /** Absent on every scan that finished before #4937: `answerCoverage` is
     *  frozen at finalize. `covered` always agrees with `answered: true`. */
    status?: AeoCoverageStatus;
}

export interface AeoAnswerCoverage {
    coveragePct: number;
    answered: number;
    total: number;
    threshold: number;
    /** The questions the site does not answer, worst first — the raw material
     *  for a "write this page" draft (#3804). */
    gaps: AeoCoverageGap[];
    /** Every query with its closest page, answered or not (#3817). The source
     *  of the question list's "Your site" column. */
    matches?: AeoCoverageGap[];
}

// ─── Tracker (ourteam-backend#3686) ──────────────────────────────────────────
// `serialize_tracker()` was written against the FE's own BusinessTracker shape,
// so this is a near pass-through rather than a translation. Typed here anyway:
// services/aeo must not import from oura/*, and a shared shape that only one
// side declares is how the two drift.

/** Who moved a task into done: a person by their role, or Oura's own closers. */
export interface AeoTaskDoneBy {
    kind: "staff" | "client" | "service" | "oura";
    userId: string | null;
}

/**
 * The re-measure of a finished task, on the measure it was raised on and only
 * against a reading taken the same way. `not_comparable` carries a `reason`
 * and no `after`: the two readings would not mean the same thing.
 */
export interface AeoTaskImpact {
    measure: "pages_failing" | "questions_named" | "search_position";
    outcome: "confirmed" | "not_yet" | "regressed" | "not_comparable";
    before: number | null;
    after: number | null;
    /** `questions_named` only: how many questions were compared. */
    of: number | null;
    reason:
        | "method_changed"
        | "questions_changed"
        | "crawl_blocked"
        | "crawl_failed"
        | "check_not_measured"
        | "withheld_by_google"
        | null;
    /** The run "before" comes from; null for `search_position`. */
    baselineAssessmentId: string | null;
    /** The run "after" comes from; null for `search_position`. */
    assessmentId: string | null;
    checkedAt: string;
    /** The backend's fixed sentence for the reading. */
    sentence: string;
}

export interface AeoTrackerTask {
    id: string;
    action: string;
    notes: string;
    tool: string;
    /** How we verify it is genuinely done, not just marked done. */
    gate: string;
    owner: string;
    status: string;
    /** True when Oura itself completes the work and flips the status. */
    auto: boolean;
    /** What this task was generated to fix (#3817) — a losing query, an audit
     *  check, or manual. Present on every task the BE serializes. */
    source?: { type: string; ref: string };
    sourceQueries?: string[];
    /**
     * Which template this task is an instance of — stable by construction, and
     * what a deliverable line joins on (#4795). `"contradiction"` is the one the
     * frontend switches on (#4837, K-2).
     */
    templateKey?: string | null;
    /**
     * Free-shaped: each rule that raises a task decides its own evidence, and
     * `{}` is the normal case. On a contradiction task this is the block both
     * quotes live in — read it through `contradictionEvidence()` rather than
     * casting, so a different rule's evidence renders as an ordinary task.
     */
    evidence?: Record<string, unknown>;
    cause?: string;
    targetPage?: string;
    /**
     * An object, not a sentence: `{assessmentId, at, reason}` from a run that
     * closed the task, `{source: "gsc", at, reason}` from the Search Console
     * check. Only on tasks the platform closed.
     */
    closedBy?: { at: string; reason?: string; assessmentId?: string; source?: string };
    /** `access` | `review` | `handover` on the three client moments (#4476),
     *  null on every other task. */
    touchpoint?: string | null;
    // The four below are OPTIONAL and absent on live today; absent means
    // unknown, and the Fixes screen says so rather than inventing a value.
    /** Channels the work moves. BE handoff: "Serialize task.helps". */
    helps?: ("ai" | "google")[];
    /** When the task moved into done, and who moved it. BE handoff: "Tracker:
     *  record when and by whom a task was done". */
    doneAt?: string | null;
    doneBy?: AeoTaskDoneBy | null;
    /** The same-method re-measure of finished work. Same handoff. */
    impact?: AeoTaskImpact | null;
    /**
     * The pages this task's audit check flagged (#4353) — the worklist, sent
     * WITH the task rather than left to be joined.
     *
     * `serialize_tracker()` guarantees these ARE the matching check's failing
     * pages, same URLs, same order, and already filtered — so nothing here needs
     * `isFailingPage` applied again. A task with no page-scoped work gets `[]`
     * explicitly, never omitted: "no pages" and "not sent" must not be spelled
     * the same way, because only one of them means the plan is complete.
     */
    affectedPages?: AeoTrackerAffectedPage[];
    /** `affectedPages.length`, sent so a row can state the size without
     *  walking the array. */
    affectedPageCount?: number;
    /** When this task is due (#4508). Always present: falls back to the block's
     *  milestone date, with `targetDateSource` saying which it is. */
    targetDate?: string | null;
    targetDateSource?: "task" | "block" | null;
}

/**
 * Where a generated fact came from (ourteam-backend#4836, K-1).
 *
 * The knowledge store holds what a merchant told us that their website does not
 * publish — a price list, a treatment menu, a note about opening hours. Every
 * fact a generator takes from one of those comes back with the passage it came
 * from, VERIFIED VERBATIM against the stored text before it is sent.
 *
 * So the frontend never checks a quote, and must never INVENT a chip by
 * matching a filename to a sentence that looks related: the backend drops a
 * citation whose quote is not in the source character-for-character, and "close
 * to something the client wrote" is precisely the claim a chip must not make.
 *
 * EVERYTHING HERE IS CLIENT-ASSERTED, NEVER MEASURED. A merchant's own document
 * may never move a number reported as measured; it feeds writing only. That is
 * also why `content_as_of` is on the chip: a price list is true for a date, and
 * the date is part of the claim.
 *
 * snake_case because the server sends the verified citation verbatim
 * (`verify_citation` in `dependencies/aeo_knowledge.py`).
 */
export interface Citation {
    source_id: string;
    /** "Price list 2026.pdf" — the file as the merchant named it. */
    source_title: string;
    /** "prices" | "menu" | "faq" | "policy" | "note" | … or null. */
    label: string | null;
    /** ISO date the CONTENT is true for, not the upload date. Null when unknown. */
    content_as_of: string | null;
    /** Verbatim from the document. */
    quote: string;
    /** Offsets into that source's text, rewritten by the server from what it
     *  actually found — the quote is the evidence, these are a convenience. */
    start: number;
    end: number;
    /**
     * The cited source has been deleted since the draft was written. PROPOSED
     * on ourteam-backend#4966: today the server drops such a citation
     * instead of flagging it. Optional and read as `=== true` only, so an older
     * payload renders exactly as before.
     */
    deleted?: boolean;
}

/**
 * What a generation read from the knowledge store, and what did not fit
 * (`selection_report` in `dependencies/aeo_knowledge.py`). Sent inside
 * `guidance_json.knowledge` on a remediation draft. camelCase because that is
 * how the server writes this one.
 */
export interface KnowledgeReadReport {
    used: { id: string; title: string; label: string | null; chars: number; contentAsOf: string | null }[];
    leftOut: { id: string; title: string; label: string | null; chars: number }[];
    budgetChars: number;
    usedChars: number;
}

/** One page under a tracker task (#4353). */
export interface AeoTrackerAffectedPage {
    url: string;
    title: string;
    /** Cross-run state, same vocabulary as the audit's own pages. */
    status?: AeoAuditPageStatus;
    /** The check's own extra column for this page (status code, word count…). */
    meta?: string;
    /** An ALREADY GENERATED draft for this page and issue, if one exists — so
     *  the drawer offers the existing work instead of quietly paying for it
     *  twice. Absent when nothing has been drafted yet. */
    draftId?: string;
}

export interface AeoTrackerWeek {
    week: number;
    title: string;
    goal: string;
    deliverable: string;
    dates: string;
    tasks: AeoTrackerTask[];
    /** How many weeks this block spans (#4517). Default 1. */
    weeks?: number;
    /** "Kickoff", "Week 1", "Weeks 2 to 4" — computed by the BE from position
     *  and length, never typed, so changing a length cannot strand a label. */
    label?: string;
}

export interface AeoScoreboardFactor {
    key: string;
    label: string;
    baseline: string;
    target: string;
    /** `null` until re-measured — never 0, which would read as a result. */
    result: string | null;
    note: string;
}

export interface AeoFixRegisterRow {
    issueKey: string;
    issue: string;
    detectedWhere: string;
    fixedAt: string;
    status: string;
    note: string;
}

/** One entry in a plan's delivery record (ourteam-backend#4508).
 *
 * Its own table server-side, not a field on the tracker blob, because a rebuild
 * replaces that blob wholesale — a work record kept inside it would be erased by
 * the act of re-planning. Append-only: a correction is a new entry. */
export interface AeoActivityEntry {
    id: string;
    /** Null for a plan-level note that belongs to no single task. */
    taskId: string | null;
    /** The stable join the BE uses to carry work across re-assessments, so an
     *  entry written against a task survives that task being renamed or reissued. */
    taskIdentity: string | null;
    week: number | null;
    assessmentId: string;
    at: string;
    author: {
        /** A display and filter label ONLY. Claude-in-Chrome calls the API as the
         *  signed-in user, so this cannot be verified and never authorises. */
        kind: "claude" | "user" | "oura";
        userId: string | null;
        label: string | null;
    };
    outcome: "done" | "started" | "blocked" | "open" | "note";
    body: string;
    evidence: Record<string, unknown>;
}

/** Body for appending one entry. `at` and the author id are set by the server. */
export interface AeoActivityCreate {
    body: string;
    outcome?: AeoActivityEntry["outcome"];
    authorKind?: AeoActivityEntry["author"]["kind"];
    authorLabel?: string;
    evidence?: Record<string, unknown>;
}

/** Result of POST /assessments/{id}/tracker/rebuild (ourteam-backend#4497). */
export interface AeoTrackerRebuild {
    tracker: AeoTracker;
    rebuilt: {
        tasksAdded: number;
        tasksDropped: number;
        droppedTasks: { action: string; status: string; source: { type?: string; ref?: string } }[];
        /** Dropped tasks somebody had already started. The number worth surfacing:
         *  everything else the rebuild changes is work nobody had touched. */
        droppedWithProgress: number;
        progressInherited: number;
        previousBlockCount: number;
        blockCount: number;
        previousTemplateVersion: number;
        templateVersion: number;
    };
}

export interface AeoTracker {
    reference: string;
    packageName: string;
    window: string;
    currentWeek: number;
    nextDeliverable: string;
    scoreboard: AeoScoreboardFactor[];
    weeks: AeoTrackerWeek[];
    fixRegister: AeoFixRegisterRow[];
    /** When the work actually starts (#4477). Every date in the plan counts
     *  forward from it in working days. */
    startDate?: string | null;
    /** Whether a person set the start date or it was defaulted, and the sentence
     *  saying so. A defaulted date must never be shown as a chosen one. */
    startDateSource?: "operator" | "default" | null;
    startDateNote?: string | null;
    holidays?: string[];
    holidaySource?: "explicit" | "computed" | null;
    /** When this plan was last BUILT, which is not when the scan ran (#4497).
     *  Null on a blob stored before the field existed. */
    generatedAt?: string | null;
    generatedFrom?: string | null;
    /** Bumped when the plan template changes, so the UI can say a newer plan is
     *  available rather than leaving the operator to guess. */
    templateVersion?: number;
    /** Work the rules propose and a human has not yet decided on (#4513).
     *  Absent on a plan built before the queue existed. */
    suggestions?: AeoTrackerSuggestion[];
}

/**
 * One proposal from the suggestion queue (ourteam-backend#4513).
 *
 * Rules propose, a human accepts. Hand-typing a task was never an option:
 * `carry_forward_progress` carries `status`, `closedBy` and `removed` and
 * nothing else, so a typed task is silently deleted by the next rebuild. An
 * ACCEPTED suggestion stays generator-derived, so a rebuild reproduces it.
 *
 * The decision outlives the plan — it is stored in its own table keyed by
 * business, not inside `tracker_json`, because a rebuild replaces that blob
 * wholesale and would erase the dismissal that stops the work being re-proposed.
 */
export interface AeoTrackerSuggestion {
    /** Deterministic: the same finding on the next run is the same suggestion,
     *  so a decision about it still applies. */
    id: string;
    rule: string;
    evidenceKey?: string;
    week: number;
    action: string;
    notes: string;
    /**
     * Why this is being proposed, with the real numbers behind it.
     *
     * Not decoration. It is what makes a proposal trustworthy rather than
     * another thing on a list, and it is the same sentence that reaches the
     * client, so it must be shown wherever the proposal is.
     */
    why: string;
    evidence?: { urls?: string[]; titles?: string[]; [k: string]: unknown };
    state: "proposed" | "kept" | "dismissed";
}

/** What keep/dismiss hand back: the decision, and the REGENERATED plan.
 *
 *  The whole tracker rather than one task, because a kept suggestion becomes a
 *  real task through the generator — a caller that patched its own copy would
 *  be a second source of truth about the same decision. */
export interface AeoSuggestionDecision {
    suggestionId: string;
    rule: string;
    state: "kept" | "dismissed";
    tracker: AeoTracker;
}

/**
 * GET /assessments/{id}/activity/summary (ourteam-backend#4531 §3) — the
 * material a weekly client update is assembled from.
 *
 * NOT a rendering of the log. The real artifact is a client-facing document
 * with a reference number, a rationale citing Search Console figures and an
 * approval checklist, and it may well stay a skill rather than become a screen.
 * So this answers the three questions such a document asks and hands the
 * entries back as evidence, rather than turning them into sentences — which
 * would be the API deciding how a client is spoken to.
 */
export interface AeoActivitySummary {
    closed: AeoActivityEntry[];
    blocked: AeoActivityEntry[];
    inFlight: AeoActivityEntry[];
    notes: AeoActivityEntry[];
    counts: { closed: number; blocked: number; inFlight: number; notes: number };
    /** What the client has to answer before anything else can move. Its own key
     *  because the document has an amber block for exactly this. */
    needsClientDecision: AeoActivityEntry[];
    range: { from: string | null; to: string | null };
}

/**
 * GET /assessments/{id}/tracker/summary (ourteam-backend#4445) — the task list
 * WITHOUT generating one.
 *
 * The full `/tracker` route mints and persists a four-week plan on first fetch,
 * so it cannot be used to merely ASK whether a plan covers something: the Site
 * Audit drawer naming the task a fix closes would have manufactured a tracker
 * for every business anyone browsed, including ones nobody has been sold.
 *
 * `exists: false` with an empty list is the answer when no plan has been
 * generated. It is not an error, and must not be rendered as one.
 */
/**
 * `GET /api/v1/assessments/{id}/audit` (ourteam-backend#5910), staff and service
 * only: the result of the automatic audit every finished scan and every
 * transplant gets on the server.
 */
export type AeoScanAuditStatus = "clean" | "issues" | "error" | "not_audited";

export interface AeoScanAuditIssue {
    /** The question's slot number. `0` is the scan as a whole. */
    n: number;
    rule: string;
    detail: string;
}

export interface AeoScanAuditNote {
    /** The question's slot number, as for an issue. */
    n: number;
    /** An observation, not a failure: "no AI Overview shown for this search". */
    note: string;
}

export interface AeoScanAudit {
    assessment_id: string;
    status: AeoScanAuditStatus;
    /** When the audit ran. Absent or null on `not_audited`. */
    checkedAt: string | null;
    /** What triggered it: `scan`, or `transplant:app_capture`. */
    trigger: string | null;
    issues: AeoScanAuditIssue[];
    notes: AeoScanAuditNote[];
    /** What the audit tidied up by itself: "ChatGPT text tidied (12 answers)". */
    fixed: string[];
    seconds: number | null;
}

export interface AeoTrackerSummary {
    exists: boolean;
    tasks: AeoTrackerSummaryTask[];
}

/** Deliberately narrower than `AeoTrackerTask`: `source` is what consumers join
 *  on, and the rest (tools, gates, scoreboard) is the Tracker tab's business. */
export interface AeoTrackerSummaryTask {
    id: string;
    action: string;
    week: number;
    status: string;
    source: { type: string; ref: string | null };
}

/**
 * Body for PATCH /assessments/{id}/tracker (ourteam-backend#4477, #4517).
 *
 * Plan SETTINGS: when the work happens. A different operation from rebuild,
 * which changes which tasks exist — the backend is explicit that a date change
 * touches no recorded progress, no week move and no removal.
 */
export interface AeoTrackerPatchBody {
    /** YYYY-MM-DD. Every date in the plan counts forward from it in working days. */
    startDate?: string;
    /** The movable Malaysian holidays cannot be derived, so they are data.
     *  Sending the list replaces it; omitting it keeps what is configured. */
    holidays?: string[];
    /** Per-block length (#4517). Only the blocks named change; one left out
     *  keeps its current length. Weekly is every block at 1. */
    phases?: { week: number; weeks: number }[];
}

/** Body for PATCH /tracker/tasks/{id} — every field optional; send only what changed. */
export interface AeoTrackerTaskPatch {
    status?: string;
    /** 1-4. Moves the task to another week. */
    /** Must be a block the plan HAS. The BE validates against its own
     *  `weeksMeta` rather than a literal range (#4498), because a plan minted
     *  before Kickoff existed would otherwise swallow the task. */
    week?: number;
    removed?: boolean;
    /** `""` clears it back to the block's milestone date (#4508). */
    targetDate?: string;
}

// ─── Remediation (ourteam-backend#3804) ──────────────────────────────────────

export interface AeoDraft {
    id: string;
    businessDomain: string;
    trackerTaskId: string | null;
    pageUrl: string;
    issueKey: string;
    draftType: string;
    draft: string;
    rationale: string;
    /** Step-by-step instructions for a draft we cannot write ourselves. */
    guidance: Record<string, unknown>;
    status: string;
    /** False = paste-it-yourself; we have no way to write this type. */
    publishable: boolean;
    createdBy: string;
    createdAt: string | null;
}

export interface AeoPublication {
    id: string;
    draftId: string;
    businessDomain: string;
    connector: string;
    target: string | null;
    status: string;
    error: string | null;
    approvedBy: string | null;
    publishedAt: string | null;
    revertedBy: string | null;
    revertedAt: string | null;
    /** Only a published change with a captured previous value can be rolled back. */
    reversible: boolean;
}

export interface AeoDraftCreateRequest {
    page_url: string;
    issue_key: string;
    draft_type: string;
    tracker_task_id?: string;
    /** From `answerCoverage.gaps` — turns "the site never answers this" into a
     *  brief for a new page rather than an edit to an existing one. */
    unanswered_questions?: string[];
}

/**
 * The outcome of fetching one business.
 *
 * A missing business and a broken request are DIFFERENT ANSWERS and must not
 * share a representation. Collapsing both to `null` meant a transient 5xx, a
 * dropped connection or an expired session looked exactly like "no such
 * business", and the caller redirected the user away from a business that
 * exists — hiding the real error and making the drill-in look permanently gone.
 *
 * Modelled as a union rather than a `null | "error"` sentinel to match the
 * existing convention in this file (`AeoCreateResult`) and because two nullish
 * meanings side by side are exactly what caused the bug.
 */
export type AeoBusinessDetailResult =
    | { ok: true; detail: AeoBusinessDetail }
    /** A real 404 — this id does not resolve. Redirecting away is correct. */
    | { ok: false; kind: "notFound" }
    /** Anything else: network, 5xx, expired auth. Retryable; never a redirect. */
    | { ok: false; kind: "error" };

/**
 * One row of `results.audit.crawledPages[]` — the crawl inventory (#3957),
 * built by `build_audit_block()` in app/dependencies/aeo_audit.py.
 *
 * This is the seam between the Pages tab and the Site Audit tab. Both are views
 * of ONE crawl: Site Audit pivots it by defect ("12 pages are missing a meta
 * description"), Pages pivots it by page ("what is wrong with the homepage").
 * Before this was read, the Pages table sourced its findings from
 * `businessAudit.underperforming[]` instead — which the BE derives from only
 * THREE of the eleven checks and caps at twelve rows, so the two tabs described
 * the same crawl differently and neither could reach the other.
 */
export interface AeoCrawledPage {
    url: string;
    /** HTTP status the crawler got. 200 unless the page is broken/redirected. */
    status?: number;
    contentType?: string;
    title?: string;
    /** schema.org types found on the page; empty array = markup absent. */
    schemaTypes?: string[];
    /** **The join.** Every audit check key this page failed, sorted. An empty
     *  array means the page was read and passed everything — which is a
     *  measurement, and different from a page that was never fetched. */
    failed?: string[];
    /** content | taxonomy | archive | paginated | feed | search | placeholder */
    pageKind?: PageKind;
    /** When this page's HTML was requested — proof a re-run read the live page. */
    fetchedAt?: string;
}

/**
 * One stored role correction — `GET /api/v1/businesses/{id}/pages/roles` (#4368 §2).
 *
 * Only corrections are returned. A page nobody has labelled is ABSENT rather
 * than present with a null role, because "no opinion recorded" and "recorded as
 * nothing" are different facts: the table renders its own inferred role for the
 * first, and must not be told that inference was overruled.
 */
export interface AeoPageRoleOverride {
    /** A PATH, not an absolute url — matching what `PATCH` stores and echoes. */
    url: string;
    role: string;
    /** Always `manual`: this table only ever holds corrections. */
    role_source?: string;
    /** Who set the label. The first question asked when somebody disagrees. */
    updated_by?: string | null;
    updated_at?: string | null;
}

/** `GET /api/v1/businesses/{id}` — one business plus its latest assessment IN
 *  FULL (the same `results` blocks `GET /assessments/{id}` returns), so a
 *  detail screen can load from a business id alone. */
/**
 * One terminal assessment in a business's score series (ourteam-backend#4572).
 *
 * Two semantics carry real weight and are easy to lose in rendering:
 *   • `gated: true` runs are INCLUDED with null scores rather than dropped — a
 *     run that produced no usable score is a real event, and hiding it leaves an
 *     unexplained gap in the timeline.
 *   • `0` is a real score. It must never be conflated with `null`, which is why
 *     both are typed rather than collapsed into a single number.
 */
/**
 * What a run cost us, in money and in tokens (#3571, BE ourteam-backend#4642).
 *
 * Every field is nullable, and the distinction is load-bearing: `null` means we
 * have no record, which is NOT the same as a run that genuinely cost nothing.
 * A run from before the backend started counting must never read as `$0.00`.
 */
export interface AeoRunCost {
    /** Recorded spend. Accumulates only from the providers that report a cost,
     *  so treat it as "what we know we spent", not a guaranteed total. */
    actual_usd: number | null;
    /** A flat per-scan figure, not derived from this run. Only ever shown
     *  labelled as an estimate. */
    estimated_usd: number | null;
}

/** One place money was recorded as spent on a client (ourteam-backend#6689). */
export interface AeoSpendSource {
    key: "scans" | "docSummaries" | "skillRuns";
    label: string;
    totalUsd: number;
    /** How many rows there are, priced or not. */
    items: number;
    /** Rows with no recorded figure: counted here, never summed as $0. */
    unknown: number;
}

/** One week, Monday to Sunday Malaysia time. `start` is the Monday, YYYY-MM-DD. */
export interface AeoSpendWeek {
    start: string;
    totalUsd: number;
    bySource: Record<AeoSpendSource["key"], number>;
}

/** GET /businesses/{id}/spend (ourteam-backend#6689): what is recorded as spent on one client. Staff only. */
export interface AeoBusinessSpend {
    currency: "USD";
    totalUsd: number;
    sources: AeoSpendSource[];
    /** Oldest first, ending with this week; an empty week is 0. */
    weeks: AeoSpendWeek[];
    thisWeekUsd: number;
    lastWeekUsd: number | null;
    /** Paid steps that record no cost yet, so the figure is read as recorded, not the whole cost. */
    notCounted: string[];
}

export interface AeoRunTokens {
    input: number | null;
    output: number | null;
    total: number | null;
}

export interface AeoScoreHistoryEntry {
    assessment_id: string;
    captured_at: string;
    /**
     * The scan's V number (ourteam-backend#5900): its place among the
     * business's scored scans, oldest first. Null for a run with no report
     * (failed, cancelled, partial, gated). Absent from an older backend.
     */
    scan_number?: number | null;
    aeo_score: number | null;
    seo_score: number | null;
    gated: boolean;
    /**
     * How the run ended: `complete`, `failed`, `cancelled` (ourteam-frontend#3933).
     *
     * The chart only ever plotted scores, so it never needed this. A version
     * list does: a run that was cancelled is part of the business's history and
     * has to be listed, but it must never be offered as a scored report.
     */
    status?: string;
    /**
     * When the client could first see this scan (ourteam-backend#6225). Null
     * on a finished scan staff have not published: the client's own history
     * leaves it out. Absent from an older backend, read as published.
     */
    published_at?: string | null;
    /** Absent on a backend that predates #4642 — the row then renders exactly
     *  as it always did, with no placeholder and no empty column. */
    cost?: AeoRunCost | null;
    tokens?: AeoRunTokens | null;
    /**
     * Questions named on this scan, overall and per engine, and the question
     * set it was measured on. `aiVisibility.trend` counts MENTIONS (answers
     * summed across engines), a different unit that cannot be split by engine,
     * so "named over time" needs this. Absent means not recorded.
     * BE handoff: "Named-over-time history".
     */
    visibility?: AeoScanVisibility | null;
}

/** One scan's named counts, as the score history would carry them. */
export interface AeoScanVisibility {
    questionsNamed: number | null;
    questionsOf: number | null;
    engines: {
        chatgpt: { named: number; answered: number; instrument?: "chatgpt_app" | "openai" } | null;
        gemini: { named: number; answered: number } | null;
        googleAi: { named: number; answered: number; asked: number } | null;
    };
    recipe: string | null;
    queriesFrozenAt: string | null;
    /**
     * The question set this scan asked, as a hash of its text: two scans
     * share it only when their questions are identical. Null when the scan
     * has no questions. BE handoff: "Score history: questions named per scan".
     */
    questionSetId?: string | null;
    /** This scan's own `queries_trend_break`: set when it was measured
     *  differently from the scan before it. Same handoff. */
    /** `codes` (ourteam-backend#5914): why, as codes a client can be shown. `reason` is the staff note. */
    trendBreak?: { at: string; reason: string; codes?: string[] } | null;
    /**
     * How this scan was measured differently with the SAME questions (where,
     * how or by which model it was asked, the counting rule, a re-ask copy):
     * notes, never a break (ourteam-backend#6400, #6382). Absent before then.
     */
    measureNotes?: { at: string; reason: string; code?: string }[];
}

export interface AeoBusinessDetail extends Omit<AeoBusiness, "latest_assessment"> {
    latest_assessment:
        | (Omit<AeoBusinessLatestAssessment, "captured_at"> & {
            /**
             * The two shapes are NOT the same block, and this is where #3510 came
             * from. The LIST serializer (`_score_point`) synthesises
             * `captured_at` as `_iso(completed_at or created_at)`; the DETAIL
             * endpoint serialises the assessment row directly and sends
             * `completed_at` / `created_at` with no `captured_at` at all.
             * Declaring it required here made `la.captured_at` type-check while
             * always being `undefined` at runtime, so every live business read
             * as "Never scanned". Optional, with the two real fields beside it.
             */
            captured_at?: string;
            completed_at?: string;
            created_at?: string;
            results?: {
                businessAudit?: AeoBusinessAudit;
                audit?: {
                    checks: AeoAuditCheck[];
                    /** Lives here, NOT on businessAudit — the number of pages the
                     *  crawler actually fetched. */
                    pagesCrawled?: number;
                    /** Every address the crawl knew about: read, queued, or
                     *  declared in the sitemap. Always sent by current runs and
                     *  stable between runs by design — prefer it over
                     *  `truncation.unvisited`, which counts the live crawl
                     *  queue and is NOT stable between runs. */
                    pagesKnown?: number;
                    /** True when the crawl stopped before reading every known
                     *  address (page ceiling or time budget). */
                    truncated?: boolean;
                    /** Why and where the crawl stopped; null when nothing was
                     *  cut. `unvisited` is queue state — do not use it as a
                     *  denominator (see `pagesKnown`). */
                    truncation?: {
                        reason: "max_pages" | "time_budget";
                        limit: number;
                        crawled: number;
                        unvisited: number;
                    } | null;
                    siteHealth?: number | null;
                    errors?: number;
                    warnings?: number;
                    /** Measured per-area health from the same crawl —
                     *  `build_audit_block()` in `app/dependencies/aeo_audit.py`.
                     *  Percentages are 0-100; `markup` and `robotsTxt` are the
                     *  BE's own enum strings, not booleans. Optional because
                     *  runs finalized before it existed have no block. */
                    healthByArea?: {
                        crawlability: number;
                        internalLinking: number;
                        markup: "present" | "missing";
                        robotsTxt: "found" | "not_found";
                    };
                    /** The crawl inventory (#3957) — one row per page the
                     *  crawler actually read, INCLUDING the pages that passed
                     *  everything. `failed[]` is the whole point: it is the
                     *  per-page list of check keys, which is the only thing
                     *  that joins a page to the findings about it. */
                    crawledPages?: AeoCrawledPage[];
                    /** `"visibility_only"` when the crawl did not run or read
                     *  no page at all; the block then carries zeros that were
                     *  never measured (`run_site_audit` in aeo_audit.py). */
                    mode?: string;
                    /** Why the crawl did not run, in a client's words (#4585).
                     *  Sent only with `mode: "visibility_only"`. */
                    mode_reason?: string;
                    /** When `build_audit_block()` wrote this block: the crawl's
                     *  own date, which a refresh moves without moving the scan. */
                    generated_at?: string;
                };
                aiVisibility?: AeoAiVisibility;
                pageSpeed?: AeoPageSpeed;
                /** The ~50 questions this run asked the engines (#3621). */
                queries?: AeoQuery[];
                /** A correction's live state per question number (POST …/questions/{n}/correct). */
                questionCorrections?: Record<string, AeoQuestionCorrection>;
                /** Staff verdict corrections per question number, then engine (POST …/questions/{n}/verdict). */
                verdictCorrections?: Record<string, Partial<Record<AeoVerdictEngine, AeoVerdictCorrection>>>;
                /**
                 * When this run's question set was frozen — `aeo_worker.py`
                 * stamps it whenever the set is (re-)authored. A run that
                 * reused a frozen set inherits the earlier stamp.
                 */
                queries_frozen_at?: string;
                /**
                 * The question set was RE-AUTHORED on this run, and the
                 * business had prior scans (#3539).
                 *
                 * Set only when both are true, because that is the only case
                 * where a trend line joins two points measured with different
                 * rulers. Miroku Hair Salon went 15/50 to 26/50 on a re-author
                 * with nothing about the business changing (traps.md trap 12) —
                 * a smooth line there is a claim we would have to defend.
                 */
                queries_trend_break?: {
                    /** ISO. Matches `queries_frozen_at` on the same run. */
                    at: string;
                    /** Plain-words reason, e.g. "question set re-authored onto
                     *  visibility-set-v3". */
                    reason: string;
                };
                /** Real search numbers (#3818). Absent on runs finalized before
                 *  it shipped; `{connected: false}` when GSC is not connected. */
                searchPerformance?: AeoSearchPerformance;
                /** What Google is asked about this business, matched onto the 50
                 *  (ourteam-backend#4981). `{connected: false}` when there is no
                 *  Search Console property or the deep pull has never run. */
                searchQuestions?: AeoSearchQuestions;
                /** Per-question engine answers (#3803). **Absent, not `[]`**,
                 *  when the engine never ran — so "not measured" stays
                 *  distinguishable from "measured, found nothing". */
                probes?: AeoProbe[];
                /** Google's AI Overview per question (ourteam-backend#5123).
                 *  **Absent, not `[]`**, on a scan from before it, which is why
                 *  the column can still say "not measured" for older scans.
                 *  Google is NOT in `probes[]` — see `AeoGoogleAiReading`. */
                googleAiReadings?: AeoGoogleAiReading[];
                /** Google's AI pages still being read (ourteam-backend#5619).
                 *  Absent when nothing is pending: render exactly as before. */
                googleAiPending?: AeoGoogleAiPending | null;
                /** Report metrics (#3802). Each is `null` when unmeasurable. */
                areaScores?: AeoAreaScore[];
                /** NOT written by the BE at this level. Finalize spreads the level
                 *  here as `maturity_level` / `maturity_label` (snake_case); the
                 *  camelCase fields live only in `reportMetrics` below. Kept for
                 *  the fixtures that set them. */
                maturityLevel?: number | null;
                maturityLabel?: string | null;
                /** The whole metrics block as `_ensure_report_metrics` persists it
                 *  — the one place `maturityLevel` and `levelCappedBy` actually
                 *  arrive in camelCase. */
                reportMetrics?: {
                    maturityLevel?: number | null;
                    maturityLabel?: string | null;
                    levelCappedBy?: AeoLevelCap | null;
                };
                /** 0–10. */
                aiReadability?: number | null;
                sentiment?: AeoSentiment | null;
                answerCoverage?: AeoAnswerCoverage | null;
                /** How the visibility number was measured (#3564). */
                grounding?: AeoGrounding | null;
                /**
                 * The branded report's written copy (`build_report_copy` in
                 * aeo_report.py, stored by aeo_worker.py). Only `competitors`
                 * is read here: one paragraph per rival the report names.
                 * Absent on runs whose copy step did not run.
                 */
                report_copy?: {
                    competitors?: { name: string; narrative: string }[] | null;
                } | null;
            };
        })
        | null;
    /**
     * The newest run, sent only when it is NOT the one reported above: a
     * re-scan that failed or produced nothing usable, which the page would
     * otherwise hide behind the older good result (aeo_business.py).
     */
    latest_attempt?: AeoLatestAttempt | null;
    /** The next scheduled scan (#3919). Null when auto-rescan is off or the
     *  business is not eligible: no date is promised then. */
    next_auto_scan_at?: string | null;
}

/** `latest_attempt` on the business detail: the re-scan that did not replace the reported one. */
export interface AeoLatestAttempt {
    assessment_id: string;
    status: string;
    captured_at: string | null;
    error_code: string | null;
    error_detail: string | null;
}

/** `GET /api/v1/businesses/{id}/connectors` (#3685) — `build_catalog()` in
 *  app/dependencies/aeo_connectors.py. Field names match `src/oura/connectors.ts`'s
 *  `DataConnector` exactly (same catalog, independently typed here so
 *  `services/aeo` doesn't reach into the `oura/*` feature module). */
export interface AeoConnector {
    key: string;
    label: string;
    /** `docs`: the status-only Google Docs entry (documents phase 1, handoff B1). */
    category: "search" | "cms" | "profile" | "docs";
    blurb: string;
    status: "connected" | "action_needed" | "not_connected";
    detail: string;
    lastSync: string | null;
    /** The property the grant is bound to, e.g. `sc-domain:example.com`.
     *  Proposed in the Sprint 0 diagnosis handoff (2026-08-06); optional until
     *  every environment sends it. */
    propertyUrl?: string | null;
    /** Set by the BE only after it has COMPARED the connected property against
     *  the crawled host and protocol. **Absent means not validated** — the FE
     *  reads it on an explicit `=== true` and never infers "valid" from its
     *  absence. */
    propertyMismatch?: boolean;
}

// ─── Pages Workshop (epic ourteam-backend#4104) ──────────────────────────────
//
// Field names are snake_case here because that is what the BE ships — it built
// against `docs/OURA_PAGES_WORKSHOP_BE_HANDOFF_2026-08-04.md`, which specifies
// `block_id`, `anchor_text`, `matched_text`, `role_source`. The Oura feature
// module speaks camelCase, so `src/oura/livePageWorkshop.ts` adapts between the
// two. Do NOT rename anything here to match the FE: this file is the wire.

/** One anchor already present inside a block's text (#4098). */
export interface AeoPageBlockLink {
    text: string;
    href: string;
}

/** One block of an extracted page — `GET /businesses/{id}/pages` (#4098). */
export interface AeoPageBlock {
    id: string;
    /** h4–h6 fold to `h3` server-side, so deep headings are never dropped. */
    tag: "h1" | "h2" | "h3" | "p" | "li" | "img" | "table" | "stat" | "quote";
    /** `img`: empty (alt carries the label). `table`: the caption.
     *  `stat`: the value. */
    text: string;
    alt?: string;
    /** Absolute and lazy-load resolved. **v1 sends it direct**, so `http://`
     *  images on our https origin and hotlink-blocking hosts will fail to
     *  load — the renderer's labelled placeholder is load-bearing. */
    src?: string;
    figure?: "photo" | "chart";
    label?: string;
    table?: { header: string[]; rows: string[][] };
    links?: AeoPageBlockLink[];
    /** Consecutive blocks sharing an id are one visual component (`cards:2`). */
    group?: string;
}

/** The backend's `PAGE_ROLES` enum, all nine as of ourteam-backend#4368.
 *  `PATCH /businesses/{id}/pages` 400s on anything outside it. */
export type AeoPageRole =
    | "home"
    | "guide"
    | "blog post"
    | "comparison"
    | "conversion"
    | "content page"
    | "product"
    | "utility"
    | "placeholder";

/**
 * `GET /api/v1/businesses/{id}/pages?url={page_url}` (#4098).
 *
 * Read from the LIVE page, not the crawl snapshot: every proposal is anchored
 * to a verbatim string, so the document the reviewer reads has to be the one
 * that is live now.
 */
/**
 * Whether Oura can write to this page, per change KIND (#3575, BE #4646).
 *
 * Per kind rather than one flag for the page: on the client that prompted this,
 * a meta description became writable the moment Yoast's key was registered for
 * REST while copy stayed unwritable. One boolean would be wrong for one of them.
 *
 * `detail` is the sentence the publish endpoint itself would return, so the
 * Workshop and the endpoint cannot describe the same page differently — render
 * it, do not paraphrase it.
 *
 * Absent on a backend that predates the field, and the surfaces then say nothing
 * about publishability, exactly as before.
 */
export interface AeoPagePublishable {
    /** False = paste-it-yourself. */
    copy: boolean;
    /** The publish endpoint's own error code, e.g. `page_built_by_builder`. */
    reason: string | null;
    /** Reader-facing sentence. Shown verbatim. */
    detail: string | null;
    /** e.g. `elementor` — the page builder that owns the layout. */
    builder: string | null;
}

export interface AeoPageContent {
    url: string;
    title: string;
    blocks: AeoPageBlock[];
    /** Can Oura write here, and if not why (#3575). */
    publishable?: AeoPagePublishable | null;
    role?: AeoPageRole;
    /** `url` < `content` < `graph` < `manual`, so the UI can tell a guess from
     *  a measurement — and `manual` from anything Oura inferred. */
    role_source?: "url" | "content" | "graph" | "manual";
    /** A notice, never a limit: 6 for home/guide, 3 otherwise. Nothing
     *  penalises a page for linking well. */
    recommended_links?: number;
}

/** Why a proposal exists. `expected` never promises a number. */
export interface AeoJustification {
    reason: string;
    expected: string;
}

export type AeoPhraseMatch = "exact" | "approximate" | "missing";

export interface AeoPagePairedPage {
    url: string;
    title: string;
}

interface AeoRecommendationBase {
    id: string;
    block_id: string;
    justification: AeoJustification;
}

export interface AeoCopyRecommendation extends AeoRecommendationBase {
    kind: "copy";
    before: string;
    after: string;
}

export interface AeoLinkRecommendation extends AeoRecommendationBase {
    kind: "link";
    anchor_text: string;
    target_url: string;
    target_title: string;
    requires_new_copy: boolean;
    proposed_copy?: string | null;
    /** Re-verified against the live page on EVERY read, not just at
     *  generation — a draft written Monday must not be applied to Tuesday's
     *  wording without the reviewer being told. */
    match?: AeoPhraseMatch;
    /** The words actually on the page. This is what gets linked. */
    matched_text?: string | null;
    /** Informational. The return link is generated as an ordinary proposal on
     *  the OTHER page, so publishing stays strictly page-scoped. */
    pairs_with?: AeoPagePairedPage | null;
}

export interface AeoRepointRecommendation extends AeoRecommendationBase {
    kind: "repoint";
    anchor_text: string;
    current_url: string;
    target_url: string;
    target_title: string;
}

export interface AeoRelatedRecommendation extends AeoRecommendationBase {
    kind: "related";
    /** Closed set; an out-of-set value falls back to "Related reading". */
    label: string;
    links: AeoPagePairedPage[];
}

export type AeoPageRecommendation =
    | AeoCopyRecommendation
    | AeoLinkRecommendation
    | AeoRepointRecommendation
    | AeoRelatedRecommendation;

/** `POST/GET /api/v1/businesses/{id}/pages/draft` (#4101). */
export interface AeoPageDraft {
    recommendations: AeoPageRecommendation[];
    /** Needed by the publish call, which the FE does not make yet — the
     *  copy-publish policy question (#4100 §1) is unanswered. */
    draft_id?: string;
    url?: string;
    created_at?: string;
}

/** Steering for a redraft. Notes are resolved against the proposals they name
 *  before reaching the model — a note without its proposal is noise. */
export interface AeoPageDraftRequest {
    url: string;
    /** Cap raised 500 → 2000 (#4101 §5). */
    instructions?: string;
    notes?: { recommendation_id: string; note: string }[];
}

/** Body for `POST /businesses/{id}/pages/publish` (#4100). `dismissed` carries
 *  the proposals the reviewer opted OUT of — inclusion is the default, so what
 *  the call names is what was dropped. `approve` is added by the service. */
export interface AeoPagePublishRequest {
    url: string;
    draft_id: string;
    dismissed?: string[];
    approve?: boolean;
}

/**
 * The outcome of a publish (#4100).
 *
 * A discriminated union rather than throw/null, because the failures are
 * different CONVERSATIONS and the screen says a different thing for each:
 *
 *   - `drift` — the live page changed since the draft, so an anchored proposal
 *     no longer matches. The BE refuses the whole write (409) and names which
 *     spans moved; the fix is Redraft, not retry.
 *   - `no_connector` — no connected WordPress site, so there is nowhere to
 *     write. Framer is not a publish target in v1.
 *   - `nothing_approved` — every proposal was dismissed. Nothing to do.
 *   - `copy_blocked` — the copy-publish policy gate refused a `copy` proposal.
 *     Cannot fire while `PUBLISH_BLOCK_COPY` is on, kept so a future flip back
 *     degrades to a clear message rather than a raw 409.
 *   - `error` — anything else, message surfaced verbatim.
 *
 * The message on every failure is the BE's own sentence, shown as written: it
 * names the drifted spans, or the missing connector, and translating it would
 * throw away exactly the detail that tells the operator what to do next.
 */
export type AeoPagePublishResult =
    | {
          ok: true;
          publicationId: string;
          url: string;
          via: string;
          publishedAt: string | null;
          /** Named, never counted — the count is `changes.length`. */
          changes: AeoPageHistoryChange[];
          reversible: boolean;
      }
    | {
          ok: false;
          kind: "drift" | "no_connector" | "nothing_approved" | "copy_blocked" | "error";
          message: string;
          /** H20a: the publish gate's refusal, naming the doc that still needs the client's yes; null otherwise. */
          refusal?: PublishRefusal | null;
          /** H20a: the client's yes is recorded and nothing went live (the sample business). Said in emerald, never as an error. */
          recorded?: boolean;
      };

export interface AeoPageHistoryChange {
    kind: "copy" | "link" | "repoint" | "related";
    /** Captured at generation time so history can name the place months later,
     *  when the blocks it was anchored to no longer exist. */
    where: string;
    before?: string;
    after?: string;
    anchor_text?: string;
    target_url?: string;
    from_url?: string;
}

export interface AeoPageHistoryEntry {
    id: string;
    kind: "drafted" | "redrafted" | "published";
    at: string;
    /** "Oura" for machine drafts. */
    by: string;
    via?: string | null;
    instruction?: string | null;
    /** The UI's count is `changes.length`, so a log can never claim more than
     *  it can name. Dismissed proposals never appear — they did not happen. */
    changes: AeoPageHistoryChange[];
}

/** One edge of `GET /api/v1/businesses/{id}/link-graph` (#4099). */
export interface AeoLinkGraphEdge {
    from: string;
    to: string;
    anchor_text: string;
    /**
     * The field the whole feature rests on. A page reached only through the
     * site menu does not link there in any sense a reader would recognise, so
     * nav/header/footer/aside edges are captured but flagged, and the engine
     * ignores them when deciding what already exists.
     */
    in_body: boolean;
}

export interface AeoLinkGraphPage {
    /**
     * A site-relative PATH — "/" or "/our-story" — NOT an absolute URL.
     *
     * Verified against the staging payload for beechoo.com.my on 2026-08-11.
     * This interface previously declared `url: string`, which was wrong twice
     * over: wrong name, and wrong kind of value. Nothing caught it because
     * `getLinkGraph` casts the response instead of parsing it, so the type was
     * an assumption about the wire that no code ever tested. It stayed
     * invisible while the only consumer counted orphans; the first screen to
     * render these rows dereferenced the missing `url` and went white.
     *
     * Resolve against the business domain before comparing with any other
     * page list in this app — every other source carries absolute URLs.
     */
    path: string;
    title?: string;
    inbound?: number;
    /**
     * Inbound links from page COPY only — the number that matters.
     *
     * camelCase here, while the sibling field on `AeoLinkGraphEdge` is
     * `in_body`. The payload genuinely mixes both conventions; this is not a
     * transcription slip.
     */
    inboundInBody?: number;
    outbound?: number;
    /** Clicks from home (BFS). `null` means never reached, which is a
     *  different finding from "nine clicks deep". */
    depth?: number | null;
    orphan?: boolean;
}

export interface AeoLinkGraph {
    edges: AeoLinkGraphEdge[];
    pages?: AeoLinkGraphPage[];
    truncated?: boolean;
    capturedAt?: string;
}

/** `results.voice` (#4101 §4) — derived once per scan from the site's own
 *  pages. **Null rather than a generic profile**: "friendly and professional"
 *  describes every business alive and would make voice-matching
 *  unfalsifiable. */
export interface AeoVoiceProfile {
    summary: string;
    sampled_from: string[];
    terminology?: string[];
}

// ─── Stored answers (ourteam-frontend#3555) ──────────────────────────────────
// `results.probes[].answer` is trimmed to 600 chars because that block is
// re-fetched on every poll. The full text lives in the answer store and is
// served one question at a time from
// `GET /api/v1/assessments/{id}/answers` — the index without `question`, one
// question's bodies with it.

/** One engine's entry in the index — no answer body, just enough to render a row. */
export interface AeoAnswerEngineSummary {
    engine: string;
    mentioned: boolean;
    /** Length of the stored answer; use it to size a skeleton, not to fetch. */
    chars: number;
    /** How many sources that answer cited. */
    sources: number;
    /**
     * Where the business sat on the answer's ranked list, 1 = first
     * (ourteam-backend#4936). A positive integer or `null`, never 0.
     *
     * The ChatGPT app reads it off the Maps place cards; every other engine's
     * is the number the engine wrote beside the business in its own numbered
     * list (ourteam-backend#5411). `null` when the business was not named, or
     * was named in prose outside any list. Absent on a backend from before
     * #4936.
     */
    rank?: number | null;
    /**
     * Whether the answer had a numbered list at all (ourteam-backend#5411).
     * `false` with `rank: null` is "no list to be placed in", not "unplaced".
     */
    rank_available?: boolean;
}

export interface AeoAnswerIndexEntry {
    n: number;
    question: string;
    check?: string | null;
    group?: string | null;
    engines: AeoAnswerEngineSummary[];
}

/**
 * The index. `stored: false` means the answers were never captured (a run from
 * before this shipped, or a failed upload) — a 200, not an error, and NOT the
 * same statement as "the AI said nothing".
 */
export interface AeoAnswersIndex {
    assessment_id: string;
    stored: boolean;
    /** Which instrument these answers came from (#3564). */
    instrument?: string | null;
    /** Present when `stored` is false — why there is nothing to show. */
    reason?: string;
    questions: AeoAnswerIndexEntry[];
}

/** One engine's full answer to one question. */
export interface AeoQuestionAnswer {
    engine: string;
    mentioned: boolean;
    answer: string;
    sources: string[];
    /**
     * Set when a mention was found but deliberately not counted (the answer
     * names a different business). Shown so the number reads as checked.
     */
    droppedReason?: string | null;
    /** Set when a mention was counted through one of the business's other names. */
    recoveredAlias?: string | null;
    /**
     * Where the business came in the answer's list. The single most compelling
     * number in the product — "named" and "named first" are different results.
     * Null when it was not named, or when the answer had no ranked list.
     */
    rank?: number | null;
    /**
     * Whether a position was MEASURABLE from this answer at all
     * (ourteam-backend#5411 BE-4). Never null when the server sends it;
     * absent on a payload stored before that change.
     *
     * It separates the two reasons `rank` is null, which a screen must not
     * show as one thing:
     *   true  + null rank — the answer ranked businesses and this one is not
     *                       in the list. A real finding.
     *   false + null rank — the answer is prose or bullets, so there is no
     *                       list to have a position in. Not a finding.
     */
    rank_available?: boolean;
    /** Who ranked above them, in order. */
    competitors?: string[] | null;
    /**
     * Storage path of the capture, e.g.
     * `staging/…/aeo_app_capture/<assessment>/q01.png`.
     *
     * A STRING, not the endpoint's argument — that is the question's `n`, and
     * the screenshot route is `/app-measurement/screenshot/{n}`. Typing this as
     * a number was enough to close the render gate silently: `typeof path ===
     * 'number'` is false, so no image ever appeared and nothing said why.
     *
     * Useful as the signal that a capture EXISTS; useless as an address.
     */
    screenshotKey?: string | number | null;
    /**
     * WHEN the picture was taken (ourteam-backend#4800, M-1).
     *
     * Part of the evidence, not metadata about it: "ChatGPT said this" is a
     * claim about a MOMENT. The capture's own `at` is `queries_frozen_at` —
     * when the question set was frozen — which can be days earlier, so showing
     * that beside a screenshot would date the evidence wrongly.
     *
     * Null on a scan from before the field existed. The screenshot is still
     * worth showing; it is shown without a date rather than with a guess.
     */
    screenshotTakenAt?: string | null;
    /**
     * Provenance only. Ships with `conversationUrlViewable: false` because
     * logged out it redirects to the ChatGPT homepage — rendering it as a link
     * would look like fabricated evidence.
     */
    conversationUrl?: string | null;
    conversationUrlViewable?: boolean | null;
}

export interface AeoQuestionAnswers {
    assessment_id: string;
    stored: boolean;
    n?: number;
    question: string;
    check?: string | null;
    group?: string | null;
    answers: AeoQuestionAnswer[];
}

// ─────────────────────────────────────────────────────────────────────────────
// Knowledge sources (ourteam-backend#4749)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * A file or a block of pasted text a business gave us that its website does not
 * publish — a price list, a treatment menu, an owner's WhatsApp message about
 * opening hours.
 *
 * **A file and a note are ONE type, not two.** After ingestion they differ only
 * by `kind`, `mime` and whether an S3 object exists; nothing downstream
 * branches on it. The UI follows the store: one list, one set of counts, and an
 * entry affordance that offers both side by side rather than as a choice.
 *
 * FIELD NAMES ARE THE RESPONSE'S, NOT THE COLUMN'S. `to_dict` on
 * `AeoKnowledgeSource` emits camelCase (`sizeBytes`, `charCount`,
 * `errorDetail`), and this once mirrored the migration's snake_case instead —
 * so every one of them read `undefined`, the row's meta line rendered empty,
 * and a failed upload showed a red badge with no reason under it.
 *
 * The REQUEST bodies are the other way round: `POST .../knowledge/text` and
 * `PATCH .../knowledge/{id}` take Pydantic models whose fields are snake_case
 * (`content_as_of`). Two directions, two spellings; the FE has to match each
 * rather than pick one.
 *
 * `text_content` is deliberately absent: it is the extracted body the prompts
 * read, it can run to hundreds of kilobytes, and no screen here shows it.
 */
export interface AeoKnowledgeSource {
    id: string;
    kind: "file" | "text";
    /** A filename, or the title typed on a note. */
    title: string;
    /** menu | prices | faq | bios | brochure | policy | note | other */
    label?: string | null;
    /**
     * The date the content is true FOR, not when it was uploaded. A price list
     * goes stale; a stale price in a client draft is the expensive kind of
     * wrong, which is why this is its own field and why the UI ages it.
     */
    contentAsOf?: string | null;
    mime?: string | null;
    sizeBytes?: number | null;
    charCount?: number | null;
    /**
     * `excluded` is a real status and NOT a delete: an operator must be able to
     * park a source that is misleading the drafts without destroying the record
     * that the client sent it.
     */
    status: "pending" | "ready" | "failed" | "excluded";
    /** The backend's own sentence, rendered verbatim. Never replaced with a
     *  generic "Upload failed" — it is usually the actionable half:
     *  "This PDF is a scan. We could not read any text from it." */
    errorDetail?: string | null;
    /** The user id, not a name — see `KnowledgeRow`, which does not print one. */
    addedBy?: string | null;
    addedByRole?: string | null;
    /** True while the row is keyed on a domain whose business does not exist
     *  yet (New scan, Step 2). The scan that follows adopts it. */
    staged?: boolean | null;
    createdAt?: string | null;
    updatedAt?: string | null;
    /**
     * Which runs or documents have read this source. **Not sent by any backend
     * yet** — its consumers are explicitly out of scope on #4749 — so the chips
     * render from the field and show nothing when it is absent, rather than the
     * UI inventing a consumer list.
     */
    usedBy?: string[] | null;
}

/**
 * The caps, as the SERVER states them.
 *
 * Sent on every list read so the FE never holds a second copy of a number this
 * side enforces — the backend's own words on ourteam-backend#4749. Optional
 * because an older deploy predates the block, and a missing `limits` must read
 * as "unknown", never as zero: a budget bar of 0 would say a business is out of
 * room the moment the field is absent.
 */
export interface KnowledgeLimits {
    maxSources: number;
    maxFileBytes: number;
    maxTotalChars: number;
    usedSources: number;
    usedChars: number;
    /** The label vocabulary the server will accept. */
    labels: string[];
}

/** One list read: the rows, and what the server says the caps are. */
export interface KnowledgePage {
    sources: AeoKnowledgeSource[];
    limits?: KnowledgeLimits;
}

/**
 * What is inside one source, for reading it on screen.
 *
 * Its own read, not a field on the list: the list leaves the text out on
 * purpose (25 sources can carry two million characters). `text` is what Oura
 * read (the note itself, or what was pulled out of a file); `fileUrl` is a
 * short-lived link to the original file, null for a pasted note.
 */
export interface KnowledgeContent {
    text: string | null;
    fileUrl: string | null;
}

/** What a knowledge write came back with, per source attempted. */
export type KnowledgeAddResult =
    | { ok: true; sources: AeoKnowledgeSource[] }
    /**
     * The dedupe rule fired (409). Distinct from a plain error because it is
     * NOT a failure the operator caused or must fix — the content is already
     * here — and the row it renders says so in those words.
     */
    | { ok: false; kind: "duplicate"; title: string; message: string }
    | { ok: false; kind: "error"; title: string; message: string };

/**
 * What a staged delete came back with (ourteam-backend#4994). `message` is the
 * server's own plain sentence for a refusal (403 / 400 / 404), never a code.
 */
export type StagedDeleteResult =
    | { ok: true }
    | { ok: false; message: string };

/* ── Partial re-run (ourteam-backend#5059) ────────────────────────────────── */

/**
 * Whether a re-run asks the world again or only recomputes what is stored.
 *
 * THE BACKEND DECIDES THIS, never the caller. It knows which stages call out,
 * and a frontend keeping its own list of which stage costs money would drift
 * the first time a stage changed.
 */
export type AeoRerunKind = "recompute" | "remeasure";

export interface AeoStageRerunStart {
    jobId?: string | null;
    stages?: string[];
    kind?: AeoRerunKind | null;
    /** `0` for a pure recompute. The confirm dialog quotes it; it is never a
     *  figure written into the frontend. */
    estimatedCostUsd?: number | null;
}

export interface AeoStageRerunJob {
    status: "queued" | "running" | "done" | "failed";
    stages?: string[];
    kind?: AeoRerunKind | null;
    startedAt?: string | null;
    /** The backend's own sentence on a failure, rendered verbatim. */
    error?: string | null;
    estimatedCostUsd?: number | null;
    /** Which payload blocks this job rewrites, named by the backend. */
    blocks?: string[] | null;
    /**
     * THE REWRITTEN BLOCKS, AS EACH STAGE LANDS — `results[stage][block]`, e.g.
     * `results.crawl.aiReadability`. A job still `running` already carries the
     * stages that finished, so a card can show the new numbers instead of
     * waiting for the whole job and a refetch behind it.
     */
    results?: Record<string, Record<string, unknown>> | null;
}

/* ── Google AI refresh (ourteam-frontend#3922, ourteam-backend#5037) ───────── */

/** What, if anything, is reading Google right now. `idle` means nothing is. */
export interface AeoGoogleAiRefreshState {
    status: "idle" | "queued" | "running";
    jobId?: string | null;
    startedAt?: string | null;
}

/**
 * One read, polled to its end.
 *
 * `googleAi` on a FAILED job is the previous measurement, kept — the card must
 * go on showing it rather than blanking over a read that did not land.
 */
export interface AeoGoogleAiRefreshJob {
    status: "queued" | "running" | "done" | "failed";
    /** The backend's own sentence, rendered verbatim. */
    error?: string | null;
    startedAt?: string | null;
    mode?: string | null;
    /** The server restarted mid-read; the job is finished but nothing landed. */
    stale?: boolean;
    googleAi?: AeoEngineShare | null;
}

/** Starting one. `busy` carries the job to attach to instead of an error. */
export type AeoGoogleAiRefreshStart =
    | { ok: true; jobId: string; assessmentId?: string | null; questions?: number | null }
    /** A refresh is already running: attach to `jobId` and poll it. */
    | { ok: false; kind: "busy"; message: string; jobId?: string | null }
    /** A full scan is running and will write the row itself. */
    | { ok: false; kind: "scanning"; message: string }
    /** The route is not deployed here. Render no control at all. */
    | { ok: false; kind: "absent" }
    /**
     * The control cannot work for this business or this deployment, and the
     * backend says why: `source_not_configured` (503), `no_baseline` and
     * `no_engines_block` (422). Disabled with the reason on it, not hidden —
     * a staffer who cannot use it deserves to know which of the three it is.
     */
    | { ok: false; kind: "unavailable"; message: string; reason: string }
    | { ok: false; kind: "error"; message: string };
